/**
 * Visual "Header & Footer" chrome editor.
 *
 * Page-builder-style experience for editing the site chrome (announcement bar,
 * header navigation, footer) with a canvas preview, left zone rail, and
 * right inspector panel. Replaces the separate Navigation and Footer form
 * editors.
 *
 * Draft/publish semantics via unified chrome_config JSON columns.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type ReactNode,
} from "react";
import { Link } from "react-router";
import { useMutation, useQuery } from "@apollo/client";
import { useBlocker } from "react-router";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Monitor,
  Plus,
  Smartphone,
  Tablet,
  Trash2,
  X,
  Menu,
} from "lucide-react";
import { useActiveTenant } from "../../../auth/AuthProvider";
import { useBranding } from "../../../branding/BrandingProvider";
import { useToast } from "../../../components/ui/Toast";
import { ConfirmDialog } from "../../../components/ConfirmDialog";
import { publicSiteBase } from "../../../hooks/publicSiteUrl";
import { readableText } from "../navigation/NavPreview";
import { onColor } from "@sections/colorUtils";
import { sanitizeInlineHtml, escapeHtml } from "@sections/sanitize";
import type { FooterLayout } from "../footer/FooterPreview";
import {
  CHROME_WIDTHS,
  CHROME_WIDTH_LABELS,
  chromeContainerStyle,
  normalizeChromeWidth,
  type ChromeWidth,
} from "@sections/chromeWidth";
import { useCanvasZoom } from "../page-canvas/useCanvasZoom";
import { DEFAULT_CANVAS_NAV } from "../page-canvas/canvasDefaults";
import { InlineEditOverlay } from "../page-canvas/InlineEditOverlay";
import { GET_CHROME_CONFIG } from "./queries";
import { SAVE_CHROME_CONFIG, PUBLISH_CHROME_CONFIG } from "./mutations";
import { DestinationTypeahead } from "./DestinationTypeahead";
import "@sections/richtext.css";

// ── Types ────────────────────────────────────────────────────

type Viewport = "desktop" | "tablet" | "mobile";
type ChromeZone = "announcement" | "header" | "footer";

const VP_WIDTHS: Record<Viewport, number> = {
  desktop: 1280,
  tablet: 740,
  mobile: 396,
};

const VP_OPTIONS: { key: Viewport; icon: typeof Monitor; label: string }[] = [
  { key: "desktop", icon: Monitor, label: "Desktop" },
  { key: "tablet", icon: Tablet, label: "Tablet" },
  { key: "mobile", icon: Smartphone, label: "Mobile" },
];

interface NavItemChild {
  key: string;
  label: string;
  url: string;
  target?: string;
}

interface NavItem {
  key: string;
  label: string;
  source: "feature" | "page" | "custom";
  sourceId: string;
  primary: boolean;
  url?: string;
  children?: NavItemChild[];
}

interface NavConfig {
  items: NavItem[];
  cta: {
    label: string;
    path: string;
    ctaColor?: string;
    /** Explicit CTA text color. Unset = auto (white on the primary slot,
     * onColor(ctaColor) on a custom background) — see the live Navbar. */
    ctaTextColor?: string;
  } | null;
  sticky: boolean;
  mobileMenu: boolean;
  headerBehavior?: string;
  width: ChromeWidth;
}

interface AnnouncementConfig {
  enabled: boolean;
  message: string;
  style: "primary" | "dark" | "accent";
  ctaLabel: string;
  ctaUrl: string;
  dismissible: boolean;
}

interface FooterConfig {
  layout: FooterLayout;
  width: ChromeWidth;
  sections: { type: string; title: string }[];
  legalText: string;
  /** Overrides tenant.legalDisclaimers in the footer legal section. */
  disclaimerText?: string;
  showQuickLinks?: boolean;
  showContactInfo?: boolean;
  showSocialLinks?: boolean;
  showLegalInfo?: boolean;
  quickLinksTitle?: string;
  contactTitle?: string;
  socialTitle?: string;
  legalTitle?: string;
  sectionOrder?: string[];
  quickLinks?: { label: string; key: string }[];
  // Optional add-on sections (content lives entirely in chrome config)
  aboutTitle?: string;
  aboutText?: string;
  hoursTitle?: string;
  hoursLines?: string[];
  badgesTitle?: string;
  badges?: { label: string; url?: string; key: string }[];
  legalLinks?: { label: string; path: string; key: string }[];
}

interface ChromeConfig {
  nav: NavConfig;
  footer: FooterConfig;
  announcement: AnnouncementConfig;
}

// ── Default configs ─────────────────────────────────────────

const DEFAULT_NAV: NavConfig = {
  items: DEFAULT_CANVAS_NAV
    .filter((n) => !n.primary)
    .map((n) => ({
      key: n.key,
      label: n.label,
      source: "feature" as const,
      sourceId: n.key.replace("default-", ""),
      primary: false,
    })),
  cta: {
    label: DEFAULT_CANVAS_NAV.find((n) => n.primary)?.label ?? "Book now",
    path: "/book",
  },
  sticky: true,
  mobileMenu: true,
  width: "contained",
};

const DEFAULT_ANNOUNCEMENT: AnnouncementConfig = {
  enabled: false,
  message: "",
  style: "primary",
  ctaLabel: "",
  ctaUrl: "",
  dismissible: true,
};

const DEFAULT_FOOTER: FooterConfig = {
  layout: "columns-4",
  width: "contained",
  sections: [
    { type: "social", title: "Follow Us" },
    { type: "quickLinks", title: "Quick Links" },
    { type: "contact", title: "Contact" },
    { type: "legal", title: "Legal" },
  ],
  legalText: "",
  showQuickLinks: true,
  showContactInfo: true,
  showSocialLinks: true,
  showLegalInfo: true,
  quickLinksTitle: "Quick Links",
  contactTitle: "Contact",
  socialTitle: "Follow Us",
  legalTitle: "Legal",
  sectionOrder: ["social", "quickLinks", "contact", "legal"],
};

// ── Parsing helpers ─────────────────────────────────────────

function composeChromeFromLegacy(
  rawNav: string | null,
  rawFooter: string | null,
  rawAnnouncement: string | null,
  // Legacy columns seed defaults for missing pieces so a first-time editor
  // starts from what the live site renders. The stored CMS chrome is
  // authoritative instead (an absent CTA means the author removed it), so
  // parseChromeConfig turns seeding off to keep load -> save a no-op.
  seedDefaults = true,
): ChromeConfig {
  let nav = { ...DEFAULT_NAV };
  if (rawNav) {
    try {
      const parsed = JSON.parse(rawNav);
      const items = (parsed.items ?? []).map(
        (it: Record<string, unknown>, i: number) => ({
          key: (it.key as string) ?? `item-${i}`,
          label: (it.label as string) ?? "",
          source: (it.source as string) ?? "feature",
          sourceId: (it.sourceId as string) ?? "",
          primary: Boolean(it.primary),
          ...(it.url ? { url: it.url as string } : {}),
          ...(Array.isArray(it.children) ? { children: it.children as NavItemChild[] } : {}),
        }),
      );
      // Separate the CTA item from regular nav items.
      // If the parsed config has no non-primary items, seed from shared
      // defaults so the editor shows the same links the live site renders.
      const primaryItem = items.find((i: NavItem) => i.primary);
      const regularItems = items.filter((i: NavItem) => !i.primary);
      nav = {
        items:
          regularItems.length > 0 || !seedDefaults
            ? regularItems
            : DEFAULT_NAV.items,
        cta: primaryItem
          ? {
              label: primaryItem.label,
              path: primaryItem.url ?? "/book",
              ...(parsed.ctaColor ? { ctaColor: parsed.ctaColor as string } : {}),
              ...(parsed.ctaTextColor
                ? { ctaTextColor: parsed.ctaTextColor as string }
                : {}),
            }
          : seedDefaults
            ? DEFAULT_NAV.cta
            : null,
        sticky: parsed.headerBehavior === "sticky" || parsed.headerBehavior === "fixed" || parsed.headerBehavior === undefined,
        mobileMenu: parsed.mobileMenu !== false,
        headerBehavior: parsed.headerBehavior,
        width: normalizeChromeWidth(parsed.width),
      };
    } catch {
      // keep defaults
    }
  }

  let announcement = { ...DEFAULT_ANNOUNCEMENT };
  if (rawAnnouncement) {
    try {
      const parsed = JSON.parse(rawAnnouncement);
      announcement = {
        enabled: Boolean(parsed.enabled),
        message: (parsed.message as string) ?? "",
        style: (["primary", "dark", "accent"].includes(parsed.style)
          ? parsed.style
          : "primary") as AnnouncementConfig["style"],
        ctaLabel: (parsed.linkLabel as string) ?? "",
        ctaUrl: (parsed.linkUrl as string) ?? "",
        dismissible: parsed.dismissible !== false,
      };
    } catch {
      // keep defaults
    }
  }

  let footer = { ...DEFAULT_FOOTER };
  if (rawFooter) {
    try {
      const parsed = JSON.parse(rawFooter);
      const flat = parsed.options ? { ...parsed, ...parsed.options } : parsed;
      footer = {
        layout: flat.layout ?? "columns-4",
        width: normalizeChromeWidth(flat.width),
        sections: DEFAULT_FOOTER.sections,
        legalText: (flat.legalText as string) ?? "",
        ...(flat.disclaimerText
          ? { disclaimerText: flat.disclaimerText as string }
          : {}),
        showQuickLinks: flat.showQuickLinks ?? true,
        showContactInfo: flat.showContactInfo ?? true,
        showSocialLinks: flat.showSocialLinks ?? true,
        showLegalInfo: flat.showLegalInfo ?? true,
        quickLinksTitle: flat.quickLinksTitle ?? "Quick Links",
        contactTitle: flat.contactTitle ?? "Contact",
        socialTitle: flat.socialTitle ?? "Follow Us",
        legalTitle: flat.legalTitle ?? "Legal",
        sectionOrder: Array.isArray(flat.sectionOrder)
          ? flat.sectionOrder
          : ["social", "quickLinks", "contact", "legal"],
        quickLinks: Array.isArray(flat.quickLinks)
          ? flat.quickLinks.map((l: { label?: string; key?: string }) => ({
              label: l.label ?? "Link",
              key: l.key ?? crypto.randomUUID(),
            }))
          : [],
        aboutTitle: flat.aboutTitle,
        aboutText: flat.aboutText,
        hoursTitle: flat.hoursTitle,
        hoursLines: Array.isArray(flat.hoursLines) ? flat.hoursLines : undefined,
        badgesTitle: flat.badgesTitle,
        badges: Array.isArray(flat.badges)
          ? flat.badges.map((b: { label?: string; url?: string; key?: string }) => ({
              label: b.label ?? "Badge",
              ...(b.url ? { url: b.url } : {}),
              key: b.key ?? crypto.randomUUID(),
            }))
          : undefined,
        legalLinks: Array.isArray(flat.legalLinks)
          ? flat.legalLinks.map((l: { label?: string; path?: string; key?: string }) => ({
              label: l.label ?? "Link",
              path: l.path ?? "/",
              key: l.key ?? crypto.randomUUID(),
            }))
          : undefined,
      };
    } catch {
      // keep defaults
    }
  }

  return { nav, footer, announcement };
}

function parseChromeConfig(raw: string | null): ChromeConfig | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    // Basic shape validation
    if (typeof parsed !== "object" || parsed === null) return null;
    // chrome_config_* stores serializeForSave's output: each zone in the
    // legacy-column shape (nav items carry the CTA as a primary-flagged
    // entry, headerBehavior instead of sticky, announcement linkLabel /
    // linkUrl). Route the zones through the shared normalizer — casting
    // here loses nav.cta/sticky, and the next save then demotes the CTA
    // into a regular item and drops the color override.
    return composeChromeFromLegacy(
      parsed.nav ? JSON.stringify(parsed.nav) : null,
      parsed.footer ? JSON.stringify(parsed.footer) : null,
      parsed.announcement ? JSON.stringify(parsed.announcement) : null,
      false,
    );
  } catch {
    return null;
  }
}

/**
 * Serialize the unified chrome config back to the legacy shape for the
 * SaveChromeConfig mutation. The nav zone stores items + CTA as a flat
 * items array with the primary flag (matching the nav_config shape the
 * public site's Navbar consumes from chrome_config_published.nav).
 */
function serializeForSave(config: ChromeConfig): string {
  // Rebuild nav in the legacy items-array shape
  const navItems = [
    ...config.nav.items.map((i) => ({ ...i, primary: false })),
    ...(config.nav.cta
      ? [
          {
            key: "cta",
            label: config.nav.cta.label,
            source: "feature" as const,
            sourceId: "booking",
            primary: true,
            url: config.nav.cta.path,
          },
        ]
      : []),
  ];
  const navPayload = {
    headerBehavior: config.nav.sticky ? "sticky" : "static",
    mobileMenu: config.nav.mobileMenu,
    width: config.nav.width,
    items: navItems,
    ...(config.nav.cta?.ctaColor ? { ctaColor: config.nav.cta.ctaColor } : {}),
    ...(config.nav.cta?.ctaTextColor
      ? { ctaTextColor: config.nav.cta.ctaTextColor }
      : {}),
  };

  // Rebuild announcement in the legacy shape
  const announcementPayload = {
    enabled: config.announcement.enabled,
    message: config.announcement.message,
    style: config.announcement.style,
    linkLabel: config.announcement.ctaLabel,
    linkUrl: config.announcement.ctaUrl,
    dismissible: config.announcement.dismissible,
  };

  // Footer already stored in the flat shape
  const footerPayload = {
    layout: config.footer.layout,
    width: config.footer.width,
    legalText: config.footer.legalText,
    ...(config.footer.disclaimerText !== undefined
      ? { disclaimerText: config.footer.disclaimerText }
      : {}),
    showQuickLinks: config.footer.showQuickLinks,
    showContactInfo: config.footer.showContactInfo,
    showSocialLinks: config.footer.showSocialLinks,
    showLegalInfo: config.footer.showLegalInfo,
    quickLinksTitle: config.footer.quickLinksTitle,
    contactTitle: config.footer.contactTitle,
    socialTitle: config.footer.socialTitle,
    legalTitle: config.footer.legalTitle,
    sectionOrder: config.footer.sectionOrder,
    quickLinks: config.footer.quickLinks,
    aboutTitle: config.footer.aboutTitle,
    aboutText: config.footer.aboutText,
    hoursTitle: config.footer.hoursTitle,
    hoursLines: config.footer.hoursLines,
    badgesTitle: config.footer.badgesTitle,
    badges: config.footer.badges,
    legalLinks: config.footer.legalLinks,
  };

  return JSON.stringify({
    nav: navPayload,
    footer: footerPayload,
    announcement: announcementPayload,
  });
}

// ── Unique key helper ───────────────────────────────────────

/** Which footer accordion section owns each inline-editable canvas field. */
const FOOTER_FIELD_SECTIONS: Record<string, string> = {
  "footer-social-title": "social",
  "footer-quickLinks-title": "quickLinks",
  "footer-contact-title": "contact",
  "footer-legal-title": "legal",
  "footer-disclaimer": "legal",
  "footer-legal-text": "legal",
  "footer-about-title": "about",
  "footer-about-text": "about",
  "footer-hours-title": "hours",
  "footer-badges-title": "badges",
};

/** Legacy visibility flags for the original four footer sections. */
const FOOTER_VISIBILITY_KEYS: Record<string, keyof FooterConfig> = {
  social: "showSocialLinks",
  quickLinks: "showQuickLinks",
  contact: "showContactInfo",
  legal: "showLegalInfo",
};

/** Config key holding each section's display title. */
const FOOTER_TITLE_KEYS: Record<string, keyof FooterConfig> = {
  social: "socialTitle",
  quickLinks: "quickLinksTitle",
  contact: "contactTitle",
  legal: "legalTitle",
  about: "aboutTitle",
  hours: "hoursTitle",
  badges: "badgesTitle",
};

/**
 * How many content sections each footer layout can display — mirrors
 * LAYOUT_CAPACITY in the live Footer.jsx so the canvas previews exactly
 * what will render.
 */
const FOOTER_LAYOUT_CAPACITY: Record<string, number> = {
  "columns-4": 4,
  "columns-3": 3,
  "columns-2": 2,
  centered: Infinity,
  minimal: 0,
};

/**
 * Catalog of footer sections a tenant can add, page-editor style. Each type
 * appears at most once. The first four are the original sections; the rest
 * hold their content entirely inside the chrome footer config.
 */
const FOOTER_SECTION_CATALOG: {
  key: string;
  label: string;
  description: string;
}[] = [
  { key: "social", label: "Social links", description: "Buttons linking to your profiles" },
  { key: "quickLinks", label: "Quick links", description: "Shortcuts to pages on your site" },
  { key: "contact", label: "Contact info", description: "Phone, email, and addresses" },
  { key: "legal", label: "Legal", description: "Legal disclaimer" },
  { key: "about", label: "About blurb", description: "A short intro to your practice" },
  { key: "hours", label: "Hours", description: "When clients can reach you" },
  { key: "badges", label: "Badges & memberships", description: "Associations, certifications, awards" },
];

let nextKey = 1;
function makeKey(): string {
  return `chrome-${Date.now()}-${nextKey++}`;
}

// ── Footer layout thumbnails ────────────────────────────────

const FOOTER_LAYOUTS: { value: FooterLayout; label: string; cols: number }[] = [
  { value: "columns-4", label: "4 Columns", cols: 4 },
  { value: "columns-3", label: "3 Columns", cols: 3 },
  { value: "columns-2", label: "2 Columns", cols: 2 },
  { value: "centered", label: "Centered", cols: 0 },
  { value: "minimal", label: "Minimal", cols: 0 },
];

// ── Width picker ────────────────────────────────────────────

/**
 * Contained / Wide / Full segmented control, shared by the header and footer
 * inspectors. Mirrors the section width options so the chrome lines up with
 * the page content.
 */
function WidthPicker({
  value,
  onChange,
}: {
  value: ChromeWidth;
  onChange: (w: ChromeWidth) => void;
}) {
  return (
    <>
      <label className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
        Width
      </label>
      <div
        role="group"
        aria-label="Width"
        className="flex bg-paper-2 rounded-lg p-[3px] gap-0.5 mb-4"
      >
        {CHROME_WIDTHS.map((w) => (
          <button
            key={w}
            type="button"
            aria-pressed={value === w}
            onClick={() => onChange(w)}
            className={`flex-1 text-center py-[5px] rounded-md text-[11px] font-semibold transition-colors ${
              value === w
                ? "bg-paper-hi text-ink shadow-card"
                : "text-ink-4 hover:text-ink-3"
            }`}
          >
            {CHROME_WIDTH_LABELS[w]}
          </button>
        ))}
      </div>
    </>
  );
}

// ── Component ───────────────────────────────────────────────

export function ChromeEditor() {
  const toast = useToast();
  const tenant = useActiveTenant();
  const { branding } = useBranding();
  const primaryColor = branding?.primaryColor ?? "#2663C7";

  // ── Data loading ────────────────────────────────────────────

  const { data, loading } = useQuery(GET_CHROME_CONFIG, {
    fetchPolicy: "network-only",
  });

  // ── Editor state ────────────────────────────────────────────

  const [initialized, setInitialized] = useState(false);
  const [config, setConfig] = useState<ChromeConfig>({
    nav: { ...DEFAULT_NAV },
    footer: { ...DEFAULT_FOOTER },
    announcement: { ...DEFAULT_ANNOUNCEMENT },
  });
  const [savedConfig, setSavedConfig] = useState<string>("");
  const [publishedConfig, setPublishedConfig] = useState<string | null>(null);
  const [viewport, setViewport] = useState<Viewport>("desktop");
  const [selectedZone, setSelectedZone] = useState<ChromeZone>("header");
  const [showPublishPop, setShowPublishPop] = useState(false);
  const [expandedFooterSection, setExpandedFooterSection] = useState<string | null>(null);
  // Which header item is expanded in the inspector accordion. Nav items use
  // their `key`; the CTA row uses the sentinel "__cta__".
  const [expandedNavItem, setExpandedNavItem] = useState<string | null>(null);

  // ── Inline editing state ──────────────────────────────────────
  const [editingField, setEditingField] = useState<{
    zone: ChromeZone;
    field: string;
    targetEl: HTMLElement;
  } | null>(null);

  // Close inline editor when the selected zone changes
  useEffect(() => {
    if (editingField && editingField.zone !== selectedZone) {
      setEditingField(null);
    }
  }, [editingField, selectedZone]);

  // Initialize from server data
  const tenantData = data?.currentTenant;
  if (tenantData && !initialized) {
    const draft = parseChromeConfig(tenantData.chromeConfigDraft);
    const published = parseChromeConfig(tenantData.chromeConfigPublished);
    const initial = draft ?? composeChromeFromLegacy(
      tenantData.navConfig,
      tenantData.footerConfig,
      tenantData.announcementConfig,
    );
    setConfig(initial);
    setSavedConfig(JSON.stringify(initial));
    setPublishedConfig(published ? JSON.stringify(published) : null);
    setInitialized(true);
  }

  // ── Dirty tracking ──────────────────────────────────────────

  const currentJson = JSON.stringify(config);
  const isDirty = initialized && currentJson !== savedConfig;

  // ── Status pill ─────────────────────────────────────────────

  const { statusLabel, statusClass } = (() => {
    if (isDirty) {
      return {
        statusLabel: "Unsaved changes",
        statusClass: "bg-spark/10 text-spark border border-spark/20",
      };
    }
    if (publishedConfig && savedConfig !== publishedConfig) {
      return {
        statusLabel: "Draft",
        statusClass: "bg-amber-50 text-amber-700 border border-amber-200",
      };
    }
    return {
      statusLabel: "Live",
      statusClass: "bg-sage-s text-sage",
    };
  })();

  // ── Unsaved-changes guards ──────────────────────────────────

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const blocker = useBlocker(isDirty);

  // ── Mutations ───────────────────────────────────────────────

  const [saveDraft, { loading: saving }] = useMutation(SAVE_CHROME_CONFIG);
  const [publishMut, { loading: publishing }] = useMutation(PUBLISH_CHROME_CONFIG);

  const handleSave = useCallback(async () => {
    try {
      await saveDraft({
        variables: { config: serializeForSave(config) },
      });
      setSavedConfig(JSON.stringify(config));
      toast.push({ kind: "success", title: "Draft saved" });
    } catch (err) {
      toast.push({
        kind: "error",
        title: "Couldn't save draft",
        description: err instanceof Error ? err.message : "Unknown error",
      });
    }
  }, [config, saveDraft, toast]);

  const handlePublish = useCallback(async () => {
    try {
      // Save first if dirty
      if (isDirty) {
        await saveDraft({
          variables: { config: serializeForSave(config) },
        });
        setSavedConfig(JSON.stringify(config));
      }
      await publishMut();
      setPublishedConfig(JSON.stringify(config));
      toast.push({ kind: "success", title: "Chrome published" });
    } catch (err) {
      toast.push({
        kind: "error",
        title: "Couldn't publish",
        description: err instanceof Error ? err.message : "Unknown error",
      });
    }
  }, [config, isDirty, saveDraft, publishMut, toast]);

  // ── Config updaters ─────────────────────────────────────────

  const updateNav = useCallback(
    (patch: Partial<NavConfig>) =>
      setConfig((prev) => ({ ...prev, nav: { ...prev.nav, ...patch } })),
    [],
  );

  const updateAnnouncement = useCallback(
    (patch: Partial<AnnouncementConfig>) =>
      setConfig((prev) => ({
        ...prev,
        announcement: { ...prev.announcement, ...patch },
      })),
    [],
  );

  const updateFooter = useCallback(
    (patch: Partial<FooterConfig>) =>
      setConfig((prev) => ({
        ...prev,
        footer: { ...prev.footer, ...patch },
      })),
    [],
  );

  // ── Footer section refs + focus helpers ────────────────────────

  const footerSectionsRef = useRef<HTMLDivElement>(null);

  /** Canvas click-to-focus: select footer zone, expand section, scroll into view. */
  const focusFooterSection = useCallback(
    (sectionKey: string) => {
      setSelectedZone("footer");
      setExpandedFooterSection(sectionKey);
      requestAnimationFrame(() => {
        const el = footerSectionsRef.current?.querySelector(
          `[data-footer-section="${sectionKey}"]`,
        );
        el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      });
    },
    [],
  );

  // ── Inline editing helpers ──────────────────────────────────

  /** Resolve the current value for an active inline editing field. */
  const getEditingValue = useCallback(
    (field: string): string => {
      switch (field) {
        case "announcement-message":
          return config.announcement.message;
        case "footer-social-title":
          return config.footer.socialTitle ?? "Follow Us";
        case "footer-quickLinks-title":
          return config.footer.quickLinksTitle ?? "Quick Links";
        case "footer-contact-title":
          return config.footer.contactTitle ?? "Contact";
        case "footer-legal-title":
          return config.footer.legalTitle ?? "Legal";
        case "footer-legal-text":
          return (
            config.footer.legalText ||
            `© ${new Date().getFullYear()} ${tenantData?.legalEntityName || tenant.name}. All rights reserved.`
          );
        case "footer-disclaimer":
          return (
            config.footer.disclaimerText ??
            tenantData?.legalDisclaimers ??
            ""
          );
        case "footer-about-title":
          return config.footer.aboutTitle ?? "About";
        case "footer-about-text":
          return config.footer.aboutText ?? "";
        case "footer-hours-title":
          return config.footer.hoursTitle ?? "Hours";
        case "footer-badges-title":
          return config.footer.badgesTitle ?? "Memberships";
        default:
          return "";
      }
    },
    [config, tenantData, tenant.name],
  );

  /** Apply an inline edit change to the corresponding config field. */
  const handleEditChange = useCallback(
    (field: string, html: string) => {
      switch (field) {
        case "announcement-message":
          updateAnnouncement({ message: html });
          break;
        case "footer-social-title":
          updateFooter({ socialTitle: html });
          break;
        case "footer-quickLinks-title":
          updateFooter({ quickLinksTitle: html });
          break;
        case "footer-contact-title":
          updateFooter({ contactTitle: html });
          break;
        case "footer-legal-title":
          updateFooter({ legalTitle: html });
          break;
        case "footer-legal-text":
          updateFooter({ legalText: html });
          break;
        case "footer-disclaimer":
          updateFooter({ disclaimerText: html });
          break;
        case "footer-about-title":
          updateFooter({ aboutTitle: html });
          break;
        case "footer-about-text":
          updateFooter({ aboutText: html });
          break;
        case "footer-hours-title":
          updateFooter({ hoursTitle: html });
          break;
        case "footer-badges-title":
          updateFooter({ badgesTitle: html });
          break;
      }
    },
    [updateAnnouncement, updateFooter],
  );

  /** Activate inline editing on a canvas text element (second-click). */
  const activateInlineEdit = useCallback(
    (
      e: React.MouseEvent<HTMLElement>,
      zone: ChromeZone,
      field: string,
    ) => {
      e.stopPropagation();
      const wasSelected = selectedZone === zone;
      setSelectedZone(zone);
      // Footer fields live inside an inspector accordion section — expand it
      // so the matching form controls are visible alongside the inline edit.
      const sectionKey = FOOTER_FIELD_SECTIONS[field];
      if (zone === "footer" && sectionKey) {
        focusFooterSection(sectionKey);
      }
      if (wasSelected) {
        setEditingField({ zone, field, targetEl: e.currentTarget });
      }
    },
    [selectedZone, focusFooterSection],
  );

  // ── Nav item operations ─────────────────────────────────────

  const addNavItem = useCallback(() => {
    const key = makeKey();
    updateNav({
      items: [
        ...config.nav.items,
        { key, label: "New link", source: "custom", sourceId: "", primary: false },
      ],
    });
    // Open the new item's accordion so label/destination are ready to edit
    setExpandedNavItem(key);
  }, [config.nav.items, updateNav]);

  const removeNavItem = useCallback(
    (key: string) => {
      updateNav({ items: config.nav.items.filter((i) => i.key !== key) });
    },
    [config.nav.items, updateNav],
  );

  const updateNavItem = useCallback(
    (key: string, patch: Partial<NavItem>) => {
      updateNav({
        items: config.nav.items.map((i) =>
          i.key === key ? { ...i, ...patch } : i,
        ),
      });
    },
    [config.nav.items, updateNav],
  );

  // ── Nav drag and drop ─────────────────────────────────────

  const [navDragIndex, setNavDragIndex] = useState<number | null>(null);
  const [navHoverIndex, setNavHoverIndex] = useState<number | null>(null);
  const [navHoverPos, setNavHoverPos] = useState<"above" | "below">("below");
  const navDragCounter = useRef(0);

  const navDragStart = useCallback(
    (e: DragEvent<HTMLDivElement>, index: number) => {
      setNavDragIndex(index);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", String(index));
    },
    [],
  );
  const navDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>, index: number) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      const rect = e.currentTarget.getBoundingClientRect();
      setNavHoverPos(e.clientY < rect.top + rect.height / 2 ? "above" : "below");
      setNavHoverIndex(index);
    },
    [],
  );
  const navDragEnter = useCallback(
    (e: DragEvent<HTMLDivElement>, index: number) => {
      e.preventDefault();
      navDragCounter.current++;
      setNavHoverIndex(index);
    },
    [],
  );
  const navDragLeave = useCallback(() => {
    navDragCounter.current--;
    if (navDragCounter.current <= 0) {
      setNavHoverIndex(null);
      navDragCounter.current = 0;
    }
  }, []);
  const navDrop = useCallback(
    (e: DragEvent<HTMLDivElement>, dropIndex: number) => {
      e.preventDefault();
      navDragCounter.current = 0;
      if (navDragIndex === null || navDragIndex === dropIndex) {
        setNavDragIndex(null);
        setNavHoverIndex(null);
        return;
      }
      const next = [...config.nav.items];
      const [moved] = next.splice(navDragIndex, 1);
      let toIndex = dropIndex;
      if (navHoverPos === "below" && dropIndex < navDragIndex) toIndex++;
      else if (navHoverPos === "above" && dropIndex > navDragIndex) toIndex--;
      toIndex = Math.max(0, Math.min(next.length, toIndex));
      next.splice(toIndex, 0, moved);
      updateNav({ items: next });
      setNavDragIndex(null);
      setNavHoverIndex(null);
    },
    [navDragIndex, navHoverPos, config.nav.items, updateNav],
  );
  const navDragEnd = useCallback(() => {
    setNavDragIndex(null);
    setNavHoverIndex(null);
    navDragCounter.current = 0;
  }, []);

  // ── Footer sections: active list + add/remove ──────────────

  const footerSectionOrder = useMemo(
    () =>
      config.footer.sectionOrder ?? ["social", "quickLinks", "contact", "legal"],
    [config.footer.sectionOrder],
  );

  // Sections currently in the footer: present in the order AND not hidden by
  // a legacy visibility flag (older configs used showX: false to hide).
  const activeFooterSections = useMemo(
    () =>
      footerSectionOrder.filter((key) => {
        const visKey = FOOTER_VISIBILITY_KEYS[key];
        return visKey ? config.footer[visKey] !== false : true;
      }),
    [footerSectionOrder, config.footer],
  );

  const addFooterSection = useCallback(
    (key: string) => {
      const visKey = FOOTER_VISIBILITY_KEYS[key];
      const patch: Partial<FooterConfig> = {};
      if (!footerSectionOrder.includes(key)) {
        patch.sectionOrder = [...footerSectionOrder, key];
      }
      if (visKey) {
        (patch as Record<string, unknown>)[visKey] = true;
      }
      updateFooter(patch);
      setExpandedFooterSection(key);
    },
    [footerSectionOrder, updateFooter],
  );

  const removeFooterSection = useCallback(
    (key: string) => {
      const visKey = FOOTER_VISIBILITY_KEYS[key];
      const patch: Partial<FooterConfig> = {
        sectionOrder: footerSectionOrder.filter((k) => k !== key),
      };
      if (visKey) {
        (patch as Record<string, unknown>)[visKey] = false;
      }
      updateFooter(patch);
      setExpandedFooterSection((prev) => (prev === key ? null : prev));
    },
    [footerSectionOrder, updateFooter],
  );

  const availableFooterSections = FOOTER_SECTION_CATALOG.filter(
    (s) => !activeFooterSections.includes(s.key),
  );
  const [addSectionOpen, setAddSectionOpen] = useState(false);

  // ── Footer section drag and drop ──────────────────────────

  const [fsDragIndex, setFsDragIndex] = useState<number | null>(null);
  const [fsHoverIndex, setFsHoverIndex] = useState<number | null>(null);
  const [fsHoverPos, setFsHoverPos] = useState<"above" | "below">("below");
  const fsDragCounter = useRef(0);

  const fsDragStart = useCallback(
    (e: DragEvent<HTMLDivElement>, index: number) => {
      setFsDragIndex(index);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", String(index));
    },
    [],
  );
  const fsDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>, index: number) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      const rect = e.currentTarget.getBoundingClientRect();
      setFsHoverPos(e.clientY < rect.top + rect.height / 2 ? "above" : "below");
      setFsHoverIndex(index);
    },
    [],
  );
  const fsDragEnter = useCallback(
    (e: DragEvent<HTMLDivElement>, index: number) => {
      e.preventDefault();
      fsDragCounter.current++;
      setFsHoverIndex(index);
    },
    [],
  );
  const fsDragLeave = useCallback(() => {
    fsDragCounter.current--;
    if (fsDragCounter.current <= 0) {
      setFsHoverIndex(null);
      fsDragCounter.current = 0;
    }
  }, []);
  const fsDrop = useCallback(
    (e: DragEvent<HTMLDivElement>, dropIndex: number) => {
      e.preventDefault();
      fsDragCounter.current = 0;
      if (fsDragIndex === null || fsDragIndex === dropIndex) {
        setFsDragIndex(null);
        setFsHoverIndex(null);
        return;
      }
      // Reorder the ACTIVE list (what the inspector displays); the result
      // becomes the new sectionOrder. Hidden legacy keys drop out of the
      // order — consistent with remove(), and re-added via "Add section".
      const next = [...activeFooterSections];
      const [moved] = next.splice(fsDragIndex, 1);
      let toIndex = dropIndex;
      if (fsHoverPos === "below" && dropIndex < fsDragIndex) toIndex++;
      else if (fsHoverPos === "above" && dropIndex > fsDragIndex) toIndex--;
      toIndex = Math.max(0, Math.min(next.length, toIndex));
      next.splice(toIndex, 0, moved);
      updateFooter({ sectionOrder: next });
      setFsDragIndex(null);
      setFsHoverIndex(null);
    },
    [fsDragIndex, fsHoverPos, activeFooterSections, updateFooter],
  );
  const fsDragEnd = useCallback(() => {
    setFsDragIndex(null);
    setFsHoverIndex(null);
    fsDragCounter.current = 0;
  }, []);

  // ── Quick links CRUD ──────────────────────────────────────────

  const addQuickLink = useCallback(() => {
    const links = config.footer.quickLinks ?? [];
    updateFooter({
      quickLinks: [...links, { label: "New link", key: makeKey() }],
    });
  }, [config.footer.quickLinks, updateFooter]);

  const removeQuickLink = useCallback(
    (index: number) => {
      const links = config.footer.quickLinks ?? [];
      updateFooter({ quickLinks: links.filter((_, i) => i !== index) });
    },
    [config.footer.quickLinks, updateFooter],
  );

  const updateQuickLink = useCallback(
    (index: number, patch: Partial<{ label: string; key: string }>) => {
      const links = config.footer.quickLinks ?? [];
      updateFooter({
        quickLinks: links.map((l, i) =>
          i === index ? { ...l, ...patch } : l,
        ),
      });
    },
    [config.footer.quickLinks, updateFooter],
  );

  // ── Quick links drag and drop ─────────────────────────────────

  const [qlDragIndex, setQlDragIndex] = useState<number | null>(null);
  const [qlHoverIndex, setQlHoverIndex] = useState<number | null>(null);
  const [qlHoverPos, setQlHoverPos] = useState<"above" | "below">("below");
  const qlDragCounter = useRef(0);

  const qlDragStart = useCallback(
    (e: DragEvent<HTMLDivElement>, index: number) => {
      e.stopPropagation(); // prevent parent footer-section drag
      setQlDragIndex(index);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", String(index));
    },
    [],
  );
  const qlDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>, index: number) => {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "move";
      const rect = e.currentTarget.getBoundingClientRect();
      setQlHoverPos(e.clientY < rect.top + rect.height / 2 ? "above" : "below");
      setQlHoverIndex(index);
    },
    [],
  );
  const qlDragEnter = useCallback(
    (e: DragEvent<HTMLDivElement>, index: number) => {
      e.preventDefault();
      e.stopPropagation();
      qlDragCounter.current++;
      setQlHoverIndex(index);
    },
    [],
  );
  const qlDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.stopPropagation();
    qlDragCounter.current--;
    if (qlDragCounter.current <= 0) {
      setQlHoverIndex(null);
      qlDragCounter.current = 0;
    }
  }, []);
  const qlDrop = useCallback(
    (e: DragEvent<HTMLDivElement>, dropIndex: number) => {
      e.preventDefault();
      e.stopPropagation();
      qlDragCounter.current = 0;
      if (qlDragIndex === null || qlDragIndex === dropIndex) {
        setQlDragIndex(null);
        setQlHoverIndex(null);
        return;
      }
      const links = [...(config.footer.quickLinks ?? [])];
      const [moved] = links.splice(qlDragIndex, 1);
      let toIndex = dropIndex;
      if (qlHoverPos === "below" && dropIndex < qlDragIndex) toIndex++;
      else if (qlHoverPos === "above" && dropIndex > qlDragIndex) toIndex--;
      toIndex = Math.max(0, Math.min(links.length, toIndex));
      links.splice(toIndex, 0, moved);
      updateFooter({ quickLinks: links });
      setQlDragIndex(null);
      setQlHoverIndex(null);
    },
    [qlDragIndex, qlHoverPos, config.footer.quickLinks, updateFooter],
  );
  const qlDragEnd = useCallback(() => {
    setQlDragIndex(null);
    setQlHoverIndex(null);
    qlDragCounter.current = 0;
  }, []);

  // ── Canvas zoom ─────────────────────────────────────────────

  const canvasRef = useRef<HTMLDivElement>(null);
  const canvasZoom = useCanvasZoom(canvasRef, VP_WIDTHS[viewport]);

  // ── Canvas helpers ──────────────────────────────────────────

  const logoUrl = (() => {
    const url = tenantData?.logoUrl;
    if (!url) return null;
    return url.startsWith("/")
      ? `${publicSiteBase(tenant.domain)}${url}`
      : url;
  })();

  const footerLogoUrl = (() => {
    const url = tenantData?.footerLogoUrl ?? tenantData?.logoUrl;
    if (!url) return null;
    return url.startsWith("/")
      ? `${publicSiteBase(tenant.domain)}${url}`
      : url;
  })();

  const displayName = tenantData?.brandName || tenant.name;
  const [logoFailed, setLogoFailed] = useState(false);
  const [footerLogoFailed, setFooterLogoFailed] = useState(false);

  // Footer color derivation — match the live site's $footer-background
  // which equals $body-background-color, mapped to branding backgroundColor.
  const footerBg = branding?.backgroundColor ?? "#196891";
  const footerText = readableText(footerBg);
  const footerMuted =
    footerText === "#ffffff" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)";

  // Social links for footer preview. The tenant stores an array of
  // { platform, url } (see the Social links settings page); tolerate the
  // legacy { platform: url } object shape too.
  const parsedSocialLinks = useMemo((): { platform: string; url: string }[] => {
    const raw = tenantData?.socialLinks;
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.filter(
          (l) => l && typeof l.url === "string" && l.url.trim(),
        ).map((l) => ({ platform: l.platform ?? l.label ?? "Link", url: l.url }));
      }
      if (typeof parsed === "object" && parsed !== null) {
        return Object.entries(parsed)
          .filter(([, v]) => typeof v === "string" && (v as string).trim())
          .map(([platform, url]) => ({ platform, url: url as string }));
      }
      return [];
    } catch {
      return [];
    }
  }, [tenantData?.socialLinks]);

  // Address for footer preview
  const addressLine = useMemo(() => {
    const raw = tenantData?.addresses;
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed) || parsed.length === 0) return null;
      const first = parsed[0] as { city?: string; state?: string };
      const parts = [first.city, first.state].filter(Boolean);
      return parts.length > 0 ? parts.join(", ") : null;
    } catch {
      return null;
    }
  }, [tenantData?.addresses]);

  // Footer section title map
  const SECTION_TITLES: Record<string, string> = {
    social: config.footer.socialTitle ?? "Follow Us",
    quickLinks: config.footer.quickLinksTitle ?? "Quick Links",
    contact: config.footer.contactTitle ?? "Contact",
    legal: config.footer.legalTitle ?? "Legal",
    about: config.footer.aboutTitle ?? "About",
    hours: config.footer.hoursTitle ?? "Hours",
    badges: config.footer.badgesTitle ?? "Memberships",
  };

  const SECTION_LABELS: Record<string, string> = Object.fromEntries(
    FOOTER_SECTION_CATALOG.map((s) => [s.key, s.label]),
  );

  // Sanitized canvas HTML, memoized: this component re-renders on every
  // keystroke (TipTap onUpdate / sidebar inputs -> setConfig), so keep
  // the sanitize-html parser off that path for unchanged strings.
  const sanitizedSectionTitles = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(SECTION_TITLES).map(([k, v]) => [
          k,
          sanitizeInlineHtml(v),
        ]),
      ),
    [config.footer],
  );
  const sanitizedDisclaimer = useMemo(
    () =>
      sanitizeInlineHtml(
        config.footer.disclaimerText ?? tenantData?.legalDisclaimers ?? "",
      ),
    [config.footer.disclaimerText, tenantData?.legalDisclaimers],
  );
  const sanitizedAboutText = useMemo(
    () =>
      config.footer.aboutText ? sanitizeInlineHtml(config.footer.aboutText) : null,
    [config.footer.aboutText],
  );
  const sanitizedAnnouncementMessage = useMemo(
    () => sanitizeInlineHtml(config.announcement.message),
    [config.announcement.message],
  );
  const sanitizedLegalText = useMemo(
    () =>
      config.footer.legalText
        ? sanitizeInlineHtml(config.footer.legalText)
        : // escapeHtml, not sanitize: the company name is plain text, and
          // a sanitizer would double-encode entities it already contains.
          `&copy; ${new Date().getFullYear()} ${escapeHtml(tenantData?.legalEntityName || tenant.name)}. All rights reserved.`,
    [config.footer.legalText, tenantData?.legalEntityName, tenant.name],
  );

  // Sections the canvas actually shows: active list, sliced to the layout's
  // capacity (mirrors getEnabledSections + LAYOUT_CAPACITY in Footer.jsx).
  // Centered layout only renders social + quick links on the live site.
  const canvasFooterSections = useMemo(() => {
    if (config.footer.layout === "centered") {
      return activeFooterSections.filter(
        (k) => k === "social" || k === "quickLinks",
      );
    }
    const capacity = FOOTER_LAYOUT_CAPACITY[config.footer.layout] ?? 4;
    return activeFooterSections.slice(0, capacity);
  }, [config.footer.layout, activeFooterSections]);

  /**
   * Render one footer section for the canvas preview, mirroring the live
   * Footer.jsx blocks. Sections are clickable (focus their inspector
   * accordion); titles and text content are inline-editable.
   */
  const renderCanvasFooterSection = (key: string): ReactNode => {
    let body: ReactNode = null;
    switch (key) {
      case "social":
        body =
          parsedSocialLinks.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {parsedSocialLinks.slice(0, 6).map((link) => (
                <span
                  key={link.platform}
                  className="inline-flex items-center rounded-[4px] border px-2 py-[3px] text-[10px] font-medium"
                  style={{
                    color: footerText,
                    borderColor:
                      footerText === "#ffffff"
                        ? "rgba(255,255,255,0.3)"
                        : "rgba(0,0,0,0.2)",
                  }}
                >
                  {link.platform}
                </span>
              ))}
            </div>
          ) : (
            // Editor-only hint: live footer hides this section until
            // social links exist in settings
            <div
              className="text-[11px] italic opacity-70"
              style={{ color: footerMuted }}
            >
              Add social links in Settings
            </div>
          );
        break;
      case "quickLinks":
        body = (
          <div className="space-y-1 text-[12px]" style={{ color: footerMuted }}>
            {(config.footer.quickLinks?.length
              ? config.footer.quickLinks
              : [{ label: "About", key: "1" }, { label: "Services", key: "2" }, { label: "Blog", key: "3" }]
            )
              .slice(0, 5)
              .map((l) => (
                <div key={l.key}>{l.label}</div>
              ))}
          </div>
        );
        break;
      case "contact":
        body = (
          <div className="space-y-0.5 text-[12px]" style={{ color: footerMuted }}>
            {tenantData?.phone && <div>{tenantData.phone}</div>}
            {tenantData?.email && <div>{tenantData.email}</div>}
            {addressLine && <div>{addressLine}</div>}
          </div>
        );
        break;
      case "legal":
        body = (
          <div className="text-[12px]" style={{ color: footerMuted }}>
            <div
              data-prop="footer-disclaimer"
              className="mt-0.5 cursor-text"
              onClick={(e) =>
                activateInlineEdit(e, "footer", "footer-disclaimer")
              }
              dangerouslySetInnerHTML={{
                __html:
                  sanitizedDisclaimer ||
                  '<span style="font-style:italic;opacity:.7">Add a legal disclaimer…</span>',
              }}
            />
          </div>
        );
        break;
      case "about":
        body = (
          <div
            data-prop="footer-about-text"
            className="text-[12px] cursor-text"
            style={{ color: footerMuted }}
            onClick={(e) => activateInlineEdit(e, "footer", "footer-about-text")}
            dangerouslySetInnerHTML={{
              __html:
                sanitizedAboutText ??
                '<span style="font-style:italic;opacity:.7">Tell visitors about your practice…</span>',
            }}
          />
        );
        break;
      case "hours": {
        const lines = (config.footer.hoursLines ?? []).filter((l) => l.trim());
        body = lines.length ? (
          <div className="space-y-0.5 text-[12px]" style={{ color: footerMuted }}>
            {lines.map((l, i) => (
              <div key={i}>{l}</div>
            ))}
          </div>
        ) : (
          <div
            className="text-[11px] italic opacity-70"
            style={{ color: footerMuted }}
          >
            Add your hours in the panel
          </div>
        );
        break;
      }
      case "badges": {
        const items = config.footer.badges ?? [];
        body = items.length ? (
          <div className="space-y-1 text-[12px]" style={{ color: footerMuted }}>
            {items.map((b) => (
              <div key={b.key}>{b.label}</div>
            ))}
          </div>
        ) : (
          <div
            className="text-[11px] italic opacity-70"
            style={{ color: footerMuted }}
          >
            Add badges in the panel
          </div>
        );
        break;
      }
      default:
        return null;
    }

    return (
      <div
        key={key}
        data-footer-section={key}
        className="cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          focusFooterSection(key);
        }}
      >
        <div
          data-prop={`footer-${key}-title`}
          className="text-[13px] font-semibold mb-2 cursor-text"
          style={{ color: footerText }}
          onClick={(e) =>
            activateInlineEdit(e, "footer", `footer-${key}-title`)
          }
          dangerouslySetInnerHTML={{
            __html: sanitizedSectionTitles[key] ?? key,
          }}
        />
        {body}
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────

  if (loading && !tenantData) {
    return (
      <div className="flex h-screen items-center justify-center bg-canvas">
        <div className="text-[13px] text-ink-5">Loading chrome editor...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-canvas overflow-hidden">
      {/* ══ TOP BAR ═══════════════════════════════════════════ */}
      <header className="h-[54px] bg-paper-hi hair-b flex items-center gap-3 px-3.5 relative z-40 flex-none">
        {/* Back */}
        <Link
          to="/website/pages"
          className="flex items-center gap-1.5 text-ink-4 text-[13px] font-medium rounded-lg px-2 py-1.5 hover:bg-paper-2 hover:text-ink-2 transition-colors"
        >
          <ArrowLeft size={13} strokeWidth={2} />
          Website
        </Link>

        <span className="text-ink-6">/</span>

        <span className="text-[14px] font-semibold text-ink">
          Header &amp; Footer
        </span>

        {/* Status pill */}
        <span
          className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-[3px] rounded-full ${statusClass}`}
        >
          {isDirty && (
            <span className="w-[7px] h-[7px] rounded-full bg-spark pulse-soft" />
          )}
          {statusLabel}
        </span>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Viewport toggle */}
        <div className="flex bg-paper-2 rounded-[10px] p-[3px]">
          {VP_OPTIONS.map(({ key, icon: Icon, label }) => (
            <button
              key={key}
              title={label}
              onClick={() => setViewport(key)}
              className={`flex items-center px-2.5 py-[5px] rounded-lg transition-colors ${
                viewport === key
                  ? "bg-paper-hi text-ink-2 shadow-card"
                  : "text-ink-5 hover:text-ink-3"
              }`}
            >
              <Icon size={15} strokeWidth={1.6} />
            </button>
          ))}
        </div>

        {/* Divider */}
        <div className="w-px h-5 bg-ink-6" />

        {/* Save draft */}
        <button
          disabled={!isDirty || saving}
          onClick={handleSave}
          className="text-[13px] font-semibold rounded-[10px] px-3.5 py-2 border border-ink-6 bg-paper-hi text-ink-2 hover:bg-paper-2 disabled:opacity-45 disabled:cursor-default transition-colors"
        >
          {saving ? "Saving..." : "Save draft"}
        </button>

        {/* Publish */}
        <button
          onClick={() => setShowPublishPop(true)}
          disabled={publishing}
          className="text-[13px] font-semibold rounded-[10px] px-3.5 py-2 border border-tide bg-tide text-white hover:bg-tide-d disabled:opacity-45 disabled:cursor-default transition-colors"
        >
          {publishing ? "Publishing..." : "Publish"}
        </button>

        {/* Publish confirm */}
        <ConfirmDialog
          open={showPublishPop}
          title="Publish site chrome?"
          description={`Your header, footer, and announcement bar go live on ${tenant.domain} immediately.`}
          variant="default"
          confirmLabel="Publish"
          cancelLabel="Cancel"
          onConfirm={async () => {
            setShowPublishPop(false);
            await handlePublish();
          }}
          onCancel={() => setShowPublishPop(false)}
        />
      </header>

      {/* ══ BODY (rail + canvas + inspector) ═══════════════════ */}
      <div className="flex flex-1 min-h-0">
        {/* ── LEFT RAIL ──────────────────────────────────────── */}
        <aside
          className="w-[180px] flex-none bg-paper overflow-y-auto px-2.5 py-3.5"
          style={{ borderRight: "1px solid var(--color-ink-6)" }}
        >
          <h4 className="text-[10px] font-bold tracking-[0.08em] uppercase text-ink-5 px-2.5 mb-2">
            Site chrome
          </h4>

          {(
            [
              { zone: "announcement" as ChromeZone, label: "Announcement bar" },
              { zone: "header" as ChromeZone, label: "Header" },
              { zone: "footer" as ChromeZone, label: "Footer" },
            ] as const
          ).map(({ zone, label }) => (
            <button
              key={zone}
              onClick={() => setSelectedZone(zone)}
              className={`flex items-center gap-2 w-full px-2.5 py-2 rounded-lg text-[12.5px] font-semibold transition-colors ${
                selectedZone === zone
                  ? "bg-tide-ss text-tide"
                  : "text-ink-3 hover:bg-paper-2 hover:text-ink-2"
              }`}
            >
              <span
                className={`w-[7px] h-[7px] rounded-full ${
                  selectedZone === zone ? "bg-tide" : "bg-ink-6"
                }`}
              />
              {label}
            </button>
          ))}

          <h4 className="text-[10px] font-bold tracking-[0.08em] uppercase text-ink-5 px-2.5 mt-5 mb-2">
            Applies to
          </h4>
          <p className="px-2.5 text-[11.5px] text-ink-4 leading-relaxed">
            Every page of your site. Page content itself is edited per-page in the page builder.
          </p>
        </aside>

        {/* ── CANVAS ─────────────────────────────────────────── */}
        <main
          ref={canvasRef}
          // No padding-top here on purpose -- see EditorCanvas. Chrome insets a
          // sticky element's constraint rect by the scroll container's padding,
          // so a padding-top on the scrollport parks `sticky top-0` children
          // that far down, leaving a gap above the pinned site header. The top
          // spacing lives on the first child instead.
          className="flex-1 overflow-y-auto px-[30px] pb-20"
          style={{ scrollBehavior: "smooth" }}
          onClick={() => {
            // clicking canvas background doesn't deselect in chrome editor
          }}
        >
          {/* URL pill */}
          <div className="flex justify-center pt-6 pb-3">
            <span className="bg-paper border border-ink-6 rounded-full px-3.5 py-1 font-mono text-[11.5px] text-ink-5">
              {tenant.domain} -- every page
            </span>
          </div>

          {/* Device frame */}
          <div
            className="@container mx-auto bg-white rounded-[14px] shadow-lift overflow-hidden transition-[width] duration-[350ms] ease-[cubic-bezier(0.3,0.9,0.3,1)]"
            style={{
              width: VP_WIDTHS[viewport],
              zoom: canvasZoom,
              ["--canvas-unzoom" as string]: 1 / canvasZoom,
            }}
          >
            {/* Announcement bar zone */}
            <div
              data-section-id="chrome-announcement"
              className={`relative cursor-pointer group`}
              onClick={() => {
                setSelectedZone("announcement");
                if (editingField) setEditingField(null);
              }}
            >
              {/* Selection/hover ring */}
              <div
                aria-hidden
                className={`absolute inset-0 z-[5] pointer-events-none ring-2 ring-inset transition-shadow duration-150 ${
                  selectedZone === "announcement"
                    ? "ring-tide"
                    : "ring-transparent group-hover:ring-tide/45"
                }`}
              />
              {/* Zone label */}
              <span
                style={{ zoom: "var(--canvas-unzoom, 1)" }}
                className={`absolute top-1.5 left-1.5 z-[6] text-[9px] font-bold tracking-[0.07em] uppercase bg-tide text-white px-2 py-[2px] rounded-md pointer-events-none transition-all duration-150 font-sans ${
                  selectedZone === "announcement"
                    ? "opacity-100"
                    : "opacity-0 group-hover:opacity-100"
                }`}
              >
                Announcement
              </span>

              {/* Announcement bar content */}
              {config.announcement.enabled && config.announcement.message.trim() ? (
                <div
                  className="flex items-center justify-center gap-3 px-4 py-2"
                  style={{
                    backgroundColor:
                      config.announcement.style === "dark"
                        ? "#1a1a1a"
                        : config.announcement.style === "accent"
                          ? "var(--tenant-tertiary, #1e3a5f)"
                          : primaryColor,
                    color:
                      config.announcement.style === "primary"
                        ? readableText(primaryColor)
                        : "#ffffff",
                  }}
                >
                  <span
                    data-prop="announcement-message"
                    className="text-[13px] font-medium text-center flex-1 cursor-text"
                    onClick={(e) =>
                      activateInlineEdit(e, "announcement", "announcement-message")
                    }
                    dangerouslySetInnerHTML={{
                      __html: sanitizedAnnouncementMessage,
                    }}
                  />
                  {config.announcement.ctaUrl && config.announcement.ctaLabel && (
                    <span
                      className="flex-none rounded px-2.5 py-1 text-[11px] font-bold"
                      style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
                    >
                      {config.announcement.ctaLabel}
                    </span>
                  )}
                  {config.announcement.dismissible && (
                    <X size={14} strokeWidth={2.5} className="flex-none opacity-60" />
                  )}
                </div>
              ) : (
                <div className="px-4 py-2 text-center text-[11px] text-ink-5 bg-paper-2">
                  Announcement bar {config.announcement.enabled ? "(empty message)" : "(disabled)"}
                </div>
              )}

              {/* Inline edit overlay for announcement message */}
              {editingField?.zone === "announcement" && (
                <InlineEditOverlay
                  targetEl={editingField.targetEl}
                  propName={editingField.field}
                  value={getEditingValue(editingField.field)}
                  onChange={(html) => handleEditChange(editingField.field, html)}
                  onClose={() => setEditingField(null)}
                />
              )}
            </div>

            {/* Header zone */}
            <div
              data-section-id="chrome-header"
              className={`relative cursor-pointer group`}
              onClick={() => {
                setSelectedZone("header");
                if (editingField) setEditingField(null);
              }}
            >
              <div
                aria-hidden
                className={`absolute inset-0 z-[5] pointer-events-none ring-2 ring-inset transition-shadow duration-150 ${
                  selectedZone === "header"
                    ? "ring-tide"
                    : "ring-transparent group-hover:ring-tide/45"
                }`}
              />
              <span
                style={{ zoom: "var(--canvas-unzoom, 1)" }}
                className={`absolute top-1.5 left-1.5 z-[6] text-[9px] font-bold tracking-[0.07em] uppercase bg-tide text-white px-2 py-[2px] rounded-md pointer-events-none transition-all duration-150 font-sans ${
                  selectedZone === "header"
                    ? "opacity-100"
                    : "opacity-0 group-hover:opacity-100"
                }`}
              >
                Header
              </span>

              <div
                className="bg-white"
                style={{ borderBottom: "1px solid #e5e7eb" }}
              >
              <div
                className="flex items-center px-8 py-3"
                style={{ ...chromeContainerStyle(config.nav.width), marginInline: "auto" }}
              >
                {/* Logo */}
                {logoUrl && !logoFailed ? (
                  <img
                    src={logoUrl}
                    alt=""
                    className="h-8 w-auto object-contain"
                    style={{ maxWidth: 180 }}
                    onError={() => setLogoFailed(true)}
                  />
                ) : (
                  <span
                    className="text-[18px] font-bold"
                    style={{ color: "var(--tenant-text, #2c3e50)" }}
                  >
                    {displayName}
                  </span>
                )}

                {viewport !== "desktop" ? (
                  /* The live navbar collapses to a hamburger below the
                     desktop breakpoint — mirror that so tablet/mobile
                     previews match the real site. */
                  <span
                    className="ml-auto p-2"
                    style={{ color: "var(--tenant-text, #2c3e50)" }}
                  >
                    <Menu size={22} strokeWidth={2} />
                  </span>
                ) : (
                <div className="flex items-center gap-1 ml-auto">
                  {config.nav.items.map((item) => (
                    <span
                      key={item.key}
                      className="px-3 py-1.5 text-[14px] font-medium rounded-[7px] hover:outline hover:outline-[1.5px] hover:outline-dashed hover:outline-tide hover:outline-offset-1 cursor-pointer"
                      style={{ color: "#4b5563" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedZone("header");
                        // Focus the corresponding inspector input
                        requestAnimationFrame(() => {
                          const input = document.querySelector(
                            `[data-nav-item-key="${item.key}"] input`,
                          );
                          if (input instanceof HTMLElement) input.focus();
                        });
                      }}
                    >
                      {item.label || "Link"}
                    </span>
                  ))}
                  {config.nav.cta && (() => {
                    // Match the live nav CTA (Navbar.jsx): a custom ctaColor
                    // gets contrast-based text via the shared onColor(), while
                    // the default primary button keeps the design system's
                    // fixed white text (the "primary" slot is isDark → text-white
                    // in ctaColors.ts, and Bulma .button.is-primary renders white).
                    // Using readableText() here diverged — it went dark on the
                    // light-gold primary, so the preview didn't match the site.
                    const customColor = config.nav.cta.ctaColor;
                    const ctaBg = customColor || primaryColor;
                    const ctaText =
                      config.nav.cta.ctaTextColor ||
                      (customColor ? onColor(customColor) : "#ffffff");
                    return (
                      <span
                        className="rounded-md px-4 py-2 text-[14px] font-semibold"
                        style={{
                          backgroundColor: ctaBg,
                          color: ctaText,
                        }}
                      >
                        {config.nav.cta.label || "Book now"}
                      </span>
                    );
                  })()}
                </div>
                )}
              </div>
              </div>
            </div>

            {/* Ghost content band */}
            <div
              className="py-12 text-center font-semibold text-[12px]"
              style={{
                color: "#b9c2d4",
                background:
                  "repeating-linear-gradient(45deg, #fafbfd, #fafbfd 12px, #f4f6fa 12px, #f4f6fa 24px)",
              }}
            >
              -- your page content --
            </div>

            {/* Footer zone */}
            <div
              data-section-id="chrome-footer"
              className={`relative cursor-pointer group`}
              onClick={() => {
                setSelectedZone("footer");
                if (editingField) setEditingField(null);
              }}
            >
              <div
                aria-hidden
                className={`absolute inset-0 z-[5] pointer-events-none ring-2 ring-inset transition-shadow duration-150 ${
                  selectedZone === "footer"
                    ? "ring-tide"
                    : "ring-transparent group-hover:ring-tide/45"
                }`}
              />
              <span
                style={{ zoom: "var(--canvas-unzoom, 1)" }}
                className={`absolute top-1.5 left-1.5 z-[6] text-[9px] font-bold tracking-[0.07em] uppercase bg-tide text-white px-2 py-[2px] rounded-md pointer-events-none transition-all duration-150 font-sans ${
                  selectedZone === "footer"
                    ? "opacity-100"
                    : "opacity-0 group-hover:opacity-100"
                }`}
              >
                Footer
              </span>

              {/* Footer content */}
              <div className="py-8" style={{ backgroundColor: footerBg }}>
              <div
                className="px-10"
                style={{ ...chromeContainerStyle(config.footer.width), marginInline: "auto" }}
              >
                {config.footer.layout === "minimal" ? (
                  <div className="flex items-center justify-between">
                    <span
                      className="text-[14px] font-semibold"
                      style={{ color: footerText }}
                    >
                      {displayName}
                    </span>
                    {parsedSocialLinks.length > 0 && (
                      <div className="flex gap-1.5">
                        {parsedSocialLinks.slice(0, 4).map((link) => (
                          <span
                            key={link.platform}
                            className="inline-flex items-center rounded-[4px] border px-2 py-[3px] text-[10px] font-medium"
                            style={{
                              color: footerText,
                              borderColor:
                                footerText === "#ffffff"
                                  ? "rgba(255,255,255,0.3)"
                                  : "rgba(0,0,0,0.2)",
                            }}
                          >
                            {link.platform}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    className="grid gap-8"
                    style={{
                      gridTemplateColumns:
                        viewport !== "desktop" ||
                        config.footer.layout === "centered"
                          ? "1fr"
                          : config.footer.layout === "columns-4"
                            ? "2fr 1fr 1fr 2fr"
                            : `repeat(${
                                config.footer.layout === "columns-3"
                                  ? 3
                                  : 2
                              }, 1fr)`,
                      textAlign:
                        config.footer.layout === "centered"
                          ? "center"
                          : undefined,
                    }}
                  >
                    {/* Brand column — first active section beneath it,
                        matching the live layouts (brand + first section
                        share the lead column) */}
                    <div>
                      {footerLogoUrl && !footerLogoFailed ? (
                        <img
                          src={footerLogoUrl}
                          alt=""
                          className="h-7 w-auto object-contain mb-3"
                          style={{
                            maxWidth: 140,
                            ...(config.footer.layout === "centered"
                              ? { margin: "0 auto 12px" }
                              : {}),
                          }}
                          onError={() => setFooterLogoFailed(true)}
                        />
                      ) : (
                        <div
                          className="text-[14px] font-semibold mb-3"
                          style={{ color: footerText }}
                        >
                          {displayName}
                        </div>
                      )}
                      {config.footer.layout !== "centered" &&
                        canvasFooterSections[0] &&
                        renderCanvasFooterSection(canvasFooterSections[0])}
                    </div>

                    {/* Remaining sections in configured order */}
                    {(config.footer.layout === "centered"
                      ? canvasFooterSections
                      : canvasFooterSections.slice(1)
                    ).map((key) => renderCanvasFooterSection(key))}
                  </div>
                )}
              </div>
              </div>

              {/* System footer bar */}
              <div
                className="py-3 text-[12px]"
                style={{
                  backgroundColor: footerBg,
                  borderTop:
                    footerText === "#ffffff"
                      ? "1px solid rgba(255,255,255,0.12)"
                      : "1px solid rgba(0,0,0,0.1)",
                  color: footerMuted,
                }}
              >
              <div
                className="px-10 flex items-center justify-between"
                style={{ ...chromeContainerStyle(config.footer.width), marginInline: "auto" }}
              >
                <span
                  data-prop="footer-legal-text"
                  className="cursor-text"
                  onClick={(e) =>
                    activateInlineEdit(e, "footer", "footer-legal-text")
                  }
                  dangerouslySetInnerHTML={{ __html: sanitizedLegalText }}
                />
                <span className="flex gap-3">
                  {config.footer.legalLinks?.length ? (
                    config.footer.legalLinks.map((l) => (
                      <span key={l.key}>{l.label}</span>
                    ))
                  ) : (
                    <>
                      <span>Terms of Use</span>
                      <span>Privacy Policy</span>
                    </>
                  )}
                </span>
              </div>
              </div>

              {/* Inline edit overlay for footer fields */}
              {editingField?.zone === "footer" && (
                <InlineEditOverlay
                  targetEl={editingField.targetEl}
                  propName={editingField.field}
                  value={getEditingValue(editingField.field)}
                  onChange={(html) => handleEditChange(editingField.field, html)}
                  onClose={() => setEditingField(null)}
                />
              )}
            </div>
          </div>
        </main>

        {/* ── INSPECTOR ──────────────────────────────────────── */}
        <aside
          className="w-[288px] flex-none overflow-y-auto bg-paper-hi px-[18px] pt-[18px] pb-8"
          style={{ borderLeft: "1px solid var(--color-ink-6)" }}
        >
          {/* ── Header inspector ─────────────────────────────── */}
          {selectedZone === "header" && (
            <>
              <div className="text-[10.5px] font-bold tracking-[0.09em] uppercase text-tide mb-0.5">
                Header
              </div>
              <div className="font-display text-[19px] font-semibold mb-3.5">
                Navigation
              </div>

              {/* Logo row */}
              <label className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
                Logo
              </label>
              <div className="flex items-center gap-2 border border-ink-6 rounded-lg bg-paper px-3 py-2 mb-1">
                <span className="text-[13px] font-bold text-ink truncate">
                  {displayName}
                </span>
                <span className="ml-auto text-[10px] text-ink-5 flex-none">
                  from Branding
                </span>
              </div>
              <div className="rounded-lg bg-tide-ss px-3 py-2.5 text-[11px] text-ink-3 leading-relaxed mb-4">
                Logo and business name come from{" "}
                <Link
                  to="/website/branding"
                  className="text-tide font-bold no-underline hover:underline"
                >
                  Branding
                </Link>
                . No logo? Your business name shows as a wordmark.
              </div>

              {/* Menu items */}
              <label className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
                Menu items{" "}
                <span className="text-ink-5 normal-case font-normal">
                  (drag to reorder)
                </span>
              </label>
              <div className="space-y-1.5 mb-1">
                {config.nav.items.map((item, index) => {
                  const isDragging = navDragIndex === index;
                  const isHovered =
                    navHoverIndex === index &&
                    navDragIndex !== null &&
                    navDragIndex !== index;
                  const isExpanded = expandedNavItem === item.key;
                  const itemPath = item.url ?? `/${item.sourceId || ""}`;
                  return (
                    <div
                      key={item.key}
                      data-nav-item-key={item.key}
                      draggable
                      onDragStart={(e) => navDragStart(e, index)}
                      onDragOver={(e) => navDragOver(e, index)}
                      onDragEnter={(e) => navDragEnter(e, index)}
                      onDragLeave={navDragLeave}
                      onDrop={(e) => navDrop(e, index)}
                      onDragEnd={navDragEnd}
                      className={`relative rounded-[9px] border bg-paper transition ${
                        isDragging ? "opacity-40" : ""
                      } ${isExpanded ? "border-ink-5" : "border-ink-6 hover:border-ink-5"}`}
                    >
                      {isHovered && navHoverPos === "above" && (
                        <span className="absolute -top-[1px] left-3 right-3 h-[2px] rounded-full bg-tide" />
                      )}
                      {isHovered && navHoverPos === "below" && (
                        <span className="absolute -bottom-[1px] left-3 right-3 h-[2px] rounded-full bg-tide" />
                      )}

                      {/* Accordion header */}
                      <div className="flex items-center gap-1.5 px-2 py-[7px]">
                        <span className="flex-none cursor-grab text-ink-5 active:cursor-grabbing">
                          <GripVertical size={12} strokeWidth={2} />
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedNavItem(isExpanded ? null : item.key)
                          }
                          className="flex-1 min-w-0 flex items-center gap-1 bg-transparent border-0 cursor-pointer text-left p-0"
                        >
                          {isExpanded ? (
                            <ChevronDown size={12} strokeWidth={2} className="flex-none text-ink-4" />
                          ) : (
                            <ChevronRight size={12} strokeWidth={2} className="flex-none text-ink-4" />
                          )}
                          <span className="text-[12px] font-semibold text-ink truncate">
                            {item.label || "Untitled"}
                          </span>
                          <span className="ml-auto font-mono text-[10px] text-ink-5 truncate max-w-[88px]">
                            {itemPath}
                          </span>
                        </button>
                        <button
                          onClick={() => removeNavItem(item.key)}
                          className="flex-none text-ink-5 hover:text-rust transition-colors p-0.5 rounded"
                        >
                          <Trash2 size={11} strokeWidth={2} />
                        </button>
                      </div>

                      {/* Accordion body */}
                      {isExpanded && (
                        <div className="px-3 pb-3 pt-2 space-y-2.5 border-t border-ink-6">
                          <div>
                            <label className="block text-[10.5px] font-semibold tracking-[0.04em] uppercase text-ink-4 mb-[5px]">
                              Label
                            </label>
                            <input
                              type="text"
                              value={item.label}
                              onChange={(e) =>
                                updateNavItem(item.key, { label: e.target.value })
                              }
                              className="w-full border border-ink-6 rounded-[7px] bg-paper px-2.5 py-1.5 text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
                            />
                          </div>
                          <div>
                            <label className="block text-[10.5px] font-semibold tracking-[0.04em] uppercase text-ink-4 mb-[5px]">
                              Destination
                            </label>
                            <DestinationTypeahead
                              variant="field"
                              value={itemPath}
                              onChange={(path) =>
                                updateNavItem(item.key, {
                                  url: path,
                                  source: "custom",
                                  sourceId: "",
                                })
                              }
                              placeholder="/path"
                            />
                          </div>

                          {/* Sub-items (one-level dropdown children) */}
                          <div>
                            <label className="block text-[10.5px] font-semibold tracking-[0.04em] uppercase text-ink-4 mb-[5px]">
                              Sub-items ({item.children?.length ?? 0})
                            </label>
                            {(!item.children || item.children.length === 0) && (
                              <p className="text-[10.5px] text-ink-5 leading-relaxed mb-1.5">
                                Add sub-items to create a dropdown menu.
                              </p>
                            )}
                            <div className="space-y-1">
                              {(item.children ?? []).map((child, ci) => (
                                <div
                                  key={child.key}
                                  className="flex items-center gap-1.5 border border-ink-6 rounded-lg bg-paper-hi px-2 py-1.5"
                                >
                                  <input
                                    type="text"
                                    value={child.label}
                                    onChange={(e) =>
                                      updateNavItem(item.key, {
                                        children: (item.children ?? []).map(
                                          (c, i) =>
                                            i === ci
                                              ? { ...c, label: e.target.value }
                                              : c,
                                        ),
                                      })
                                    }
                                    placeholder="Label"
                                    className="flex-1 min-w-0 bg-transparent border-0 text-[11px] font-semibold text-ink px-0 py-0 focus:outline-none focus:ring-0"
                                  />
                                  <DestinationTypeahead
                                    value={child.url}
                                    onChange={(path) =>
                                      updateNavItem(item.key, {
                                        children: (item.children ?? []).map(
                                          (c, i) =>
                                            i === ci
                                              ? { ...c, url: path }
                                              : c,
                                        ),
                                      })
                                    }
                                    placeholder="/path"
                                    className="w-[72px] flex-none"
                                  />
                                  <button
                                    onClick={() =>
                                      updateNavItem(item.key, {
                                        children: (item.children ?? []).filter(
                                          (_, i) => i !== ci,
                                        ),
                                      })
                                    }
                                    className="flex-none text-ink-5 hover:text-rust transition-colors p-0.5 rounded"
                                  >
                                    <Trash2 size={10} strokeWidth={2} />
                                  </button>
                                </div>
                              ))}
                            </div>
                            <button
                              onClick={() =>
                                updateNavItem(item.key, {
                                  children: [
                                    ...(item.children ?? []),
                                    {
                                      key: makeKey(),
                                      label: "New sub-item",
                                      url: "/",
                                    },
                                  ],
                                })
                              }
                              className="text-[11px] font-bold text-tide hover:text-tide-d inline-flex items-center gap-1 transition-colors mt-1"
                            >
                              <Plus size={11} strokeWidth={2.5} />
                              Add sub-item
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <button
                onClick={addNavItem}
                className="text-[12px] font-bold text-rust hover:underline mb-3 inline-flex items-center gap-1"
              >
                <Plus size={12} strokeWidth={2.5} />
                Add menu item
              </button>

              {/* CTA button */}
              <label className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mt-3 mb-[7px]">
                Call-to-action button
              </label>
              {!config.nav.cta && (
                <button
                  onClick={() => {
                    updateNav({ cta: { label: "Book now", path: "/book" } });
                    setExpandedNavItem("__cta__");
                  }}
                  className="text-[12px] font-bold text-rust hover:underline mb-3 inline-flex items-center gap-1"
                >
                  <Plus size={12} strokeWidth={2.5} />
                  Add call-to-action button
                </button>
              )}
              {config.nav.cta && (
                <div
                  className={`rounded-[9px] border bg-paper mb-3 transition ${
                    expandedNavItem === "__cta__"
                      ? "border-ink-5"
                      : "border-ink-6 hover:border-ink-5"
                  }`}
                >
                  {/* Accordion header */}
                  <div className="flex items-center gap-1.5 px-2 py-[7px]">
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedNavItem(
                          expandedNavItem === "__cta__" ? null : "__cta__",
                        )
                      }
                      className="flex-1 min-w-0 flex items-center gap-1 bg-transparent border-0 cursor-pointer text-left p-0"
                    >
                      {expandedNavItem === "__cta__" ? (
                        <ChevronDown size={12} strokeWidth={2} className="flex-none text-ink-4" />
                      ) : (
                        <ChevronRight size={12} strokeWidth={2} className="flex-none text-ink-4" />
                      )}
                      <span className="text-[12px] font-semibold text-ink truncate">
                        {config.nav.cta.label || "Untitled"}
                      </span>
                      <span className="ml-auto font-mono text-[10px] text-ink-5 truncate max-w-[88px]">
                        {config.nav.cta.path}
                      </span>
                    </button>
                    <span className="text-[10px] text-ink-5 flex-none">primary</span>
                  </div>

                  {/* Accordion body */}
                  {expandedNavItem === "__cta__" && (
                    <div className="px-3 pb-3 pt-2 space-y-2.5 border-t border-ink-6">
                      <div>
                        <label className="block text-[10.5px] font-semibold tracking-[0.04em] uppercase text-ink-4 mb-[5px]">
                          Label
                        </label>
                        <input
                          type="text"
                          value={config.nav.cta.label}
                          onChange={(e) =>
                            updateNav({
                              cta: { ...config.nav.cta!, label: e.target.value },
                            })
                          }
                          className="w-full border border-ink-6 rounded-[7px] bg-paper px-2.5 py-1.5 text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-[10.5px] font-semibold tracking-[0.04em] uppercase text-ink-4 mb-[5px]">
                          Destination
                        </label>
                        <DestinationTypeahead
                          variant="field"
                          value={config.nav.cta.path}
                          onChange={(path) =>
                            updateNav({
                              cta: { ...config.nav.cta!, path },
                            })
                          }
                          placeholder="/book"
                        />
                      </div>
                      <div>
                        <label className="block text-[10.5px] font-semibold tracking-[0.04em] uppercase text-ink-4 mb-[5px]">
                          Button color
                        </label>
                        <div className="flex flex-wrap gap-1.5">
                          {([
                            { key: "", label: "Primary", color: branding?.primaryColor || "#b83333" },
                            { key: branding?.secondaryColor || "#2E8B8B", label: "Secondary", color: branding?.secondaryColor || "#2E8B8B" },
                            { key: branding?.tertiaryColor || "#1e3a5f", label: "Tertiary", color: branding?.tertiaryColor || "#1e3a5f" },
                            { key: branding?.backgroundColor || "#196891", label: "Brand", color: branding?.backgroundColor || "#196891" },
                            { key: "#2c2c2c", label: "Dark", color: "#2c2c2c" },
                          ] as const).map((swatch) => {
                            const isActive =
                              swatch.key === ""
                                ? !config.nav.cta!.ctaColor
                                : config.nav.cta!.ctaColor === swatch.key;
                            const isLight = readableText(swatch.color) !== "#ffffff";
                            return (
                              <button
                                key={swatch.key || "__default__"}
                                type="button"
                                title={`${swatch.label} (${swatch.color})`}
                                onClick={() =>
                                  updateNav({
                                    cta: {
                                      ...config.nav.cta!,
                                      ctaColor: swatch.key || undefined,
                                    },
                                  })
                                }
                                className="bg-transparent border-0 p-0 cursor-pointer"
                              >
                                <span
                                  className="block w-[28px] h-[28px] rounded-[7px] transition-all duration-100 relative"
                                  style={{
                                    backgroundColor: swatch.color,
                                    border: isActive
                                      ? "2px solid var(--color-tide)"
                                      : isLight
                                        ? "2px solid var(--color-ink-6)"
                                        : "2px solid transparent",
                                    boxShadow: isActive
                                      ? "0 0 0 2px var(--color-tide-s)"
                                      : undefined,
                                  }}
                                >
                                  {isActive && (
                                    <span className="absolute inset-0 flex items-center justify-center">
                                      <svg
                                        width="12"
                                        height="12"
                                        viewBox="0 0 14 14"
                                        fill="none"
                                        className={isLight ? "text-tide" : "text-white"}
                                      >
                                        <path
                                          d="M3 7.5L5.5 10L11 4"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    </span>
                                  )}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div>
                        <label className="block text-[10.5px] font-semibold tracking-[0.04em] uppercase text-ink-4 mb-[5px]">
                          Text color
                        </label>
                        <div className="flex flex-wrap gap-1.5">
                          {(() => {
                            const autoText = config.nav.cta!.ctaColor
                              ? onColor(config.nav.cta!.ctaColor)
                              : "#ffffff";
                            return [
                              { key: "", label: "Auto", color: autoText },
                              { key: "#ffffff", label: "White", color: "#ffffff" },
                              { key: "#1a1a1a", label: "Dark", color: "#1a1a1a" },
                            ] as const;
                          })().map((swatch) => {
                            const isActive =
                              swatch.key === ""
                                ? !config.nav.cta!.ctaTextColor
                                : config.nav.cta!.ctaTextColor === swatch.key;
                            const isLight = readableText(swatch.color) !== "#ffffff";
                            return (
                              <button
                                key={swatch.key || "__auto__"}
                                type="button"
                                title={
                                  swatch.key
                                    ? `${swatch.label} (${swatch.color})`
                                    : `Auto (${swatch.color})`
                                }
                                onClick={() =>
                                  updateNav({
                                    cta: {
                                      ...config.nav.cta!,
                                      ctaTextColor: swatch.key || undefined,
                                    },
                                  })
                                }
                                className="bg-transparent border-0 p-0 cursor-pointer"
                              >
                                <span
                                  className="block w-[28px] h-[28px] rounded-[7px] transition-all duration-100 relative"
                                  style={{
                                    backgroundColor: swatch.color,
                                    border: isActive
                                      ? "2px solid var(--color-tide)"
                                      : isLight
                                        ? "2px solid var(--color-ink-6)"
                                        : "2px solid transparent",
                                    boxShadow: isActive
                                      ? "0 0 0 2px var(--color-tide-s)"
                                      : undefined,
                                  }}
                                >
                                  {isActive && (
                                    <span className="absolute inset-0 flex items-center justify-center">
                                      <svg
                                        width="12"
                                        height="12"
                                        viewBox="0 0 14 14"
                                        fill="none"
                                        className={isLight ? "text-tide" : "text-white"}
                                      >
                                        <path
                                          d="M3 7.5L5.5 10L11 4"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    </span>
                                  )}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Width */}
              <WidthPicker
                value={config.nav.width}
                onChange={(w) => updateNav({ width: w })}
              />

              {/* Toggles */}
              <div className="flex items-center justify-between py-2 text-[12.5px] font-semibold text-ink-2">
                Sticky header
                <button
                  type="button"
                  role="switch"
                  aria-checked={config.nav.sticky}
                  onClick={() => updateNav({ sticky: !config.nav.sticky })}
                  className={`relative inline-flex h-[17px] w-[30px] flex-none items-center rounded-full transition-colors ${
                    config.nav.sticky ? "bg-tide" : "bg-ink-6"
                  }`}
                >
                  <span
                    className={`inline-block h-[13px] w-[13px] rounded-full bg-white transition-transform ${
                      config.nav.sticky ? "translate-x-[15px]" : "translate-x-[2px]"
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between py-2 text-[12.5px] font-semibold text-ink-2">
                Show on mobile as menu
                <button
                  type="button"
                  role="switch"
                  aria-checked={config.nav.mobileMenu}
                  onClick={() => updateNav({ mobileMenu: !config.nav.mobileMenu })}
                  className={`relative inline-flex h-[17px] w-[30px] flex-none items-center rounded-full transition-colors ${
                    config.nav.mobileMenu ? "bg-tide" : "bg-ink-6"
                  }`}
                >
                  <span
                    className={`inline-block h-[13px] w-[13px] rounded-full bg-white transition-transform ${
                      config.nav.mobileMenu ? "translate-x-[15px]" : "translate-x-[2px]"
                    }`}
                  />
                </button>
              </div>
            </>
          )}

          {/* ── Announcement inspector ───────────────────────── */}
          {selectedZone === "announcement" && (
            <>
              <div className="text-[10.5px] font-bold tracking-[0.09em] uppercase text-tide mb-0.5">
                Announcement bar
              </div>
              <div className="font-display text-[19px] font-semibold mb-3.5">
                Announcement
              </div>

              {/* Enabled toggle */}
              <div className="flex items-center justify-between py-2 text-[12.5px] font-semibold text-ink-2 mb-2">
                Enabled
                <button
                  type="button"
                  role="switch"
                  aria-checked={config.announcement.enabled}
                  onClick={() =>
                    updateAnnouncement({
                      enabled: !config.announcement.enabled,
                    })
                  }
                  className={`relative inline-flex h-[17px] w-[30px] flex-none items-center rounded-full transition-colors ${
                    config.announcement.enabled ? "bg-tide" : "bg-ink-6"
                  }`}
                >
                  <span
                    className={`inline-block h-[13px] w-[13px] rounded-full bg-white transition-transform ${
                      config.announcement.enabled
                        ? "translate-x-[15px]"
                        : "translate-x-[2px]"
                    }`}
                  />
                </button>
              </div>

              {/* Message */}
              <label className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
                Message
              </label>
              <input
                type="text"
                value={config.announcement.message}
                onChange={(e) =>
                  updateAnnouncement({ message: e.target.value })
                }
                placeholder="Your announcement message..."
                className="w-full border border-ink-6 rounded-lg bg-paper px-3 py-2 text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all mb-3"
              />

              {/* Style segmented */}
              <label className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
                Style
              </label>
              <div className="flex bg-paper-2 rounded-lg p-[3px] gap-0.5 mb-3">
                {(["primary", "dark", "accent"] as const).map((style) => (
                  <button
                    key={style}
                    onClick={() => updateAnnouncement({ style })}
                    className={`flex-1 text-center py-[5px] rounded-md text-[11px] font-semibold transition-colors ${
                      config.announcement.style === style
                        ? "bg-paper-hi text-ink shadow-card"
                        : "text-ink-4 hover:text-ink-3"
                    }`}
                  >
                    {style.charAt(0).toUpperCase() + style.slice(1)}
                  </button>
                ))}
              </div>

              {/* Button fields */}
              <label className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
                Button
              </label>
              <input
                type="text"
                value={config.announcement.ctaLabel}
                onChange={(e) =>
                  updateAnnouncement({ ctaLabel: e.target.value })
                }
                placeholder="Button label"
                className="w-full border border-ink-6 rounded-lg bg-paper px-3 py-2 text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all mb-1.5"
              />
              <DestinationTypeahead
                variant="field"
                value={config.announcement.ctaUrl}
                onChange={(path) => updateAnnouncement({ ctaUrl: path })}
                placeholder="/book or https://..."
                className="mb-3"
              />

              {/* Dismissible toggle */}
              <div className="flex items-center justify-between py-2 text-[12.5px] font-semibold text-ink-2">
                Visitors can dismiss
                <button
                  type="button"
                  role="switch"
                  aria-checked={config.announcement.dismissible}
                  onClick={() =>
                    updateAnnouncement({
                      dismissible: !config.announcement.dismissible,
                    })
                  }
                  className={`relative inline-flex h-[17px] w-[30px] flex-none items-center rounded-full transition-colors ${
                    config.announcement.dismissible ? "bg-tide" : "bg-ink-6"
                  }`}
                >
                  <span
                    className={`inline-block h-[13px] w-[13px] rounded-full bg-white transition-transform ${
                      config.announcement.dismissible
                        ? "translate-x-[15px]"
                        : "translate-x-[2px]"
                    }`}
                  />
                </button>
              </div>

              <div className="rounded-lg bg-tide-ss px-3 py-2.5 text-[11px] text-ink-3 leading-relaxed mt-3">
                Shown above the header on every page until dismissed.
              </div>
            </>
          )}

          {/* ── Footer inspector ─────────────────────────────── */}
          {selectedZone === "footer" && (
            <>
              <div className="text-[10.5px] font-bold tracking-[0.09em] uppercase text-tide mb-0.5">
                Footer
              </div>
              <div className="font-display text-[19px] font-semibold mb-3.5">
                Footer
              </div>

              {/* Layout picker */}
              <label className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
                Layout
              </label>
              <div className="grid grid-cols-3 gap-1.5 mb-4">
                {FOOTER_LAYOUTS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => updateFooter({ layout: opt.value })}
                    className={`border rounded-lg px-2 pt-2 pb-1.5 text-center transition-colors ${
                      config.footer.layout === opt.value
                        ? "border-primary shadow-[0_0_0_2px_rgba(184,51,51,0.15)]"
                        : "border-ink-6 bg-paper hover:border-ink-5"
                    }`}
                  >
                    <div className="flex gap-0.5 justify-center mb-1">
                      {opt.value === "centered" ? (
                        <>
                          <span className="w-5 h-1 bg-ink-5 rounded-sm" />
                          <span className="w-5 h-1 bg-ink-5 rounded-sm" />
                        </>
                      ) : opt.value === "minimal" ? (
                        <span className="w-5 h-1 bg-ink-5 rounded-sm" />
                      ) : (
                        Array.from({ length: opt.cols }).map((_, i) => (
                          <span
                            key={i}
                            className="w-1.5 h-3.5 bg-ink-5 rounded-sm"
                          />
                        ))
                      )}
                    </div>
                    <span className="text-[9.5px] font-semibold text-ink-3">
                      {opt.label}
                    </span>
                  </button>
                ))}
              </div>

              {/* Width */}
              <WidthPicker
                value={config.footer.width}
                onChange={(w) => updateFooter({ width: w })}
              />

              {/* Sections list (accordion) */}
              <label className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
                Sections{" "}
                <span className="text-ink-5 normal-case font-normal">
                  (drag to reorder)
                </span>
              </label>
              <div ref={footerSectionsRef} className="space-y-1.5 mb-3">
                {activeFooterSections.map((key, index) => {
                  const isDragging = fsDragIndex === index;
                  const isHovered =
                    fsHoverIndex === index &&
                    fsDragIndex !== null &&
                    fsDragIndex !== index;
                  const isExpanded = expandedFooterSection === key;
                  const titleKey = FOOTER_TITLE_KEYS[key];
                  const titleValue = titleKey
                    ? (config.footer[titleKey] as string) ?? SECTION_TITLES[key] ?? ""
                    : "";
                  return (
                    <div
                      key={key}
                      data-footer-section={key}
                      draggable
                      onDragStart={(e) => fsDragStart(e, index)}
                      onDragOver={(e) => fsDragOver(e, index)}
                      onDragEnter={(e) => fsDragEnter(e, index)}
                      onDragLeave={fsDragLeave}
                      onDrop={(e) => fsDrop(e, index)}
                      onDragEnd={fsDragEnd}
                      className={`relative rounded-[9px] border bg-paper transition ${
                        isDragging ? "opacity-40" : ""
                      } ${isExpanded ? "border-ink-5" : "border-ink-6 hover:border-ink-5"}`}
                    >
                      {isHovered && fsHoverPos === "above" && (
                        <span className="absolute -top-[1px] left-3 right-3 h-[2px] rounded-full bg-tide" />
                      )}
                      {isHovered && fsHoverPos === "below" && (
                        <span className="absolute -bottom-[1px] left-3 right-3 h-[2px] rounded-full bg-tide" />
                      )}

                      {/* Accordion header */}
                      <div className="flex items-center gap-1.5 px-2 py-[7px]">
                        <span className="flex-none cursor-grab text-ink-5 active:cursor-grabbing">
                          <GripVertical size={12} strokeWidth={2} />
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedFooterSection(isExpanded ? null : key)
                          }
                          className="flex-1 min-w-0 flex items-center gap-1 bg-transparent border-0 cursor-pointer text-left p-0"
                        >
                          {isExpanded ? (
                            <ChevronDown size={12} strokeWidth={2} className="flex-none text-ink-4" />
                          ) : (
                            <ChevronRight size={12} strokeWidth={2} className="flex-none text-ink-4" />
                          )}
                          <span className="text-[12px] font-semibold text-ink truncate">
                            {SECTION_LABELS[key] ?? key}
                          </span>
                        </button>
                        {/* Remove section */}
                        <button
                          type="button"
                          title={`Remove ${SECTION_LABELS[key] ?? key}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFooterSection(key);
                          }}
                          className="flex-none p-1 rounded text-ink-5 hover:text-rust transition-colors"
                        >
                          <Trash2 size={12} strokeWidth={1.8} />
                        </button>
                      </div>

                      {/* Accordion body */}
                      {isExpanded && (
                        <div className="px-3 pb-3 pt-0.5 space-y-2.5 border-t border-ink-6">
                          {/* Section title input (shared with inline canvas edit) */}
                          {titleKey && (
                            <div>
                              <label className="block text-[10.5px] font-semibold tracking-[0.04em] uppercase text-ink-4 mb-[5px]">
                                Section title
                              </label>
                              <input
                                type="text"
                                value={titleValue}
                                onChange={(e) =>
                                  updateFooter({ [titleKey]: e.target.value })
                                }
                                className="w-full border border-ink-6 rounded-[7px] bg-paper px-2.5 py-1.5 text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
                              />
                            </div>
                          )}

                          {/* Per-section content */}
                          {key === "quickLinks" && (
                            <>
                              <label className="block text-[10.5px] font-semibold tracking-[0.04em] uppercase text-ink-4 mb-[5px]">
                                Links ({config.footer.quickLinks?.length ?? 0})
                              </label>
                              {(!config.footer.quickLinks || config.footer.quickLinks.length === 0) && (
                                <p className="text-[10.5px] text-ink-5 leading-relaxed mb-1.5">
                                  Showing default links until you add your own.
                                </p>
                              )}
                              <div className="space-y-1">
                                {(config.footer.quickLinks ?? []).map((link, li) => {
                                  const qlIsDragging = qlDragIndex === li;
                                  const qlIsHovered =
                                    qlHoverIndex === li &&
                                    qlDragIndex !== null &&
                                    qlDragIndex !== li;
                                  // Use label+index as a stable key since `key` field
                                  // is the destination URL and changes as user types
                                  return (
                                    <div
                                      key={`ql-${li}`}
                                      onDragOver={(e) => qlDragOver(e, li)}
                                      onDragEnter={(e) => qlDragEnter(e, li)}
                                      onDragLeave={qlDragLeave}
                                      onDrop={(e) => qlDrop(e, li)}
                                      onDragEnd={qlDragEnd}
                                      className={`relative flex items-center gap-1.5 border border-ink-6 rounded-lg bg-paper-hi px-2 py-1.5 transition ${
                                        qlIsDragging ? "opacity-40" : ""
                                      }`}
                                    >
                                      {qlIsHovered && qlHoverPos === "above" && (
                                        <span className="absolute -top-[1px] left-2 right-2 h-[2px] rounded-full bg-tide" />
                                      )}
                                      {qlIsHovered && qlHoverPos === "below" && (
                                        <span className="absolute -bottom-[1px] left-2 right-2 h-[2px] rounded-full bg-tide" />
                                      )}
                                      <span
                                        draggable
                                        onDragStart={(e) => qlDragStart(e as unknown as DragEvent<HTMLDivElement>, li)}
                                        className="flex-none cursor-grab text-ink-5 active:cursor-grabbing"
                                      >
                                        <GripVertical size={10} strokeWidth={2} />
                                      </span>
                                      <input
                                        type="text"
                                        value={link.label}
                                        onChange={(e) =>
                                          updateQuickLink(li, { label: e.target.value })
                                        }
                                        placeholder="Label"
                                        className="flex-1 min-w-0 bg-transparent border-0 text-[11px] font-semibold text-ink px-0 py-0 focus:outline-none focus:ring-0"
                                      />
                                      <DestinationTypeahead
                                        value={link.key.startsWith("chrome-") ? "" : link.key}
                                        onChange={(path) =>
                                          updateQuickLink(li, { key: path || makeKey() })
                                        }
                                        placeholder="/path"
                                        className="w-[72px] flex-none"
                                      />
                                      <button
                                        onClick={() => removeQuickLink(li)}
                                        className="flex-none text-ink-5 hover:text-rust transition-colors p-0.5 rounded"
                                      >
                                        <Trash2 size={10} strokeWidth={2} />
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                              <button
                                onClick={addQuickLink}
                                className="text-[11px] font-bold text-tide hover:text-tide-d inline-flex items-center gap-1 transition-colors"
                              >
                                <Plus size={11} strokeWidth={2.5} />
                                Add link
                              </button>
                            </>
                          )}

                          {key === "legal" && (
                            <>
                              <div>
                                <label className="block text-[10.5px] font-semibold tracking-[0.04em] uppercase text-ink-4 mb-[5px]">
                                  Disclaimer
                                </label>
                                <textarea
                                  value={
                                    config.footer.disclaimerText ??
                                    tenantData?.legalDisclaimers ??
                                    ""
                                  }
                                  onChange={(e) =>
                                    updateFooter({ disclaimerText: e.target.value })
                                  }
                                  rows={4}
                                  placeholder="e.g. Services are provided by …"
                                  className="w-full border border-ink-6 rounded-[7px] bg-paper px-2.5 py-1.5 text-[12px] text-ink resize-y focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
                                />
                              </div>
                              <div>
                                <label className="block text-[10.5px] font-semibold tracking-[0.04em] uppercase text-ink-4 mb-[5px]">
                                  Legal text
                                </label>
                                <textarea
                                  value={
                                    config.footer.legalText ||
                                    `© ${new Date().getFullYear()} ${tenantData?.legalEntityName || tenant.name}. All rights reserved.`
                                  }
                                  onChange={(e) =>
                                    updateFooter({ legalText: e.target.value })
                                  }
                                  rows={3}
                                  className="w-full border border-ink-6 rounded-[7px] bg-paper px-2.5 py-1.5 text-[12px] text-ink resize-y focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
                                />
                              </div>
                            </>
                          )}

                          {key === "social" && (
                            <div className="rounded-lg bg-tide-ss px-3 py-2.5 text-[11px] text-ink-3 leading-relaxed">
                              Social links come from your practice settings.{" "}
                              <Link
                                to="/settings/social"
                                className="text-tide font-bold no-underline hover:underline"
                              >
                                Edit social links
                              </Link>
                            </div>
                          )}

                          {key === "contact" && (
                            <div className="rounded-lg bg-tide-ss px-3 py-2.5 text-[11px] text-ink-3 leading-relaxed">
                              Phone, email, and addresses come from your practice settings.{" "}
                              <Link
                                to="/settings/contact"
                                className="text-tide font-bold no-underline hover:underline"
                              >
                                Edit contact info
                              </Link>
                            </div>
                          )}

                          {key === "about" && (
                            <div>
                              <label className="block text-[10.5px] font-semibold tracking-[0.04em] uppercase text-ink-4 mb-[5px]">
                                Text
                              </label>
                              <textarea
                                value={config.footer.aboutText ?? ""}
                                onChange={(e) =>
                                  updateFooter({ aboutText: e.target.value })
                                }
                                rows={4}
                                placeholder="A short intro to your practice…"
                                className="w-full border border-ink-6 rounded-[7px] bg-paper px-2.5 py-1.5 text-[12px] text-ink resize-y focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
                              />
                            </div>
                          )}

                          {key === "hours" && (
                            <div>
                              <label className="block text-[10.5px] font-semibold tracking-[0.04em] uppercase text-ink-4 mb-[5px]">
                                Hours{" "}
                                <span className="text-ink-5 normal-case font-normal">
                                  (one line each)
                                </span>
                              </label>
                              <textarea
                                value={(config.footer.hoursLines ?? []).join("\n")}
                                onChange={(e) =>
                                  updateFooter({
                                    hoursLines: e.target.value.split("\n"),
                                  })
                                }
                                rows={4}
                                placeholder={"Mon–Fri: 9am–5pm\nSat: by appointment"}
                                className="w-full border border-ink-6 rounded-[7px] bg-paper px-2.5 py-1.5 text-[12px] text-ink resize-y focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
                              />
                            </div>
                          )}

                          {key === "badges" && (
                            <>
                              <label className="block text-[10.5px] font-semibold tracking-[0.04em] uppercase text-ink-4 mb-[5px]">
                                Badges ({config.footer.badges?.length ?? 0})
                              </label>
                              <div className="space-y-1">
                                {(config.footer.badges ?? []).map((badge, bi) => (
                                  <div
                                    key={badge.key}
                                    className="flex items-center gap-1.5 border border-ink-6 rounded-lg bg-paper-hi px-2 py-1.5"
                                  >
                                    <input
                                      type="text"
                                      value={badge.label}
                                      onChange={(e) =>
                                        updateFooter({
                                          badges: (config.footer.badges ?? []).map(
                                            (b, i) =>
                                              i === bi
                                                ? { ...b, label: e.target.value }
                                                : b,
                                          ),
                                        })
                                      }
                                      placeholder="State Bar of California"
                                      className="flex-1 min-w-0 bg-transparent border-0 text-[11px] font-semibold text-ink px-0 py-0 focus:outline-none focus:ring-0"
                                    />
                                    <input
                                      type="text"
                                      value={badge.url ?? ""}
                                      onChange={(e) =>
                                        updateFooter({
                                          badges: (config.footer.badges ?? []).map(
                                            (b, i) =>
                                              i === bi
                                                ? { ...b, url: e.target.value }
                                                : b,
                                          ),
                                        })
                                      }
                                      placeholder="https:// (optional)"
                                      className="w-[84px] flex-none bg-transparent border-0 text-right font-mono text-[10px] text-ink-5 px-0 py-0 focus:outline-none focus:ring-0 focus:text-ink"
                                    />
                                    <button
                                      onClick={() =>
                                        updateFooter({
                                          badges: (config.footer.badges ?? []).filter(
                                            (_, i) => i !== bi,
                                          ),
                                        })
                                      }
                                      className="flex-none text-ink-5 hover:text-rust transition-colors p-0.5 rounded"
                                    >
                                      <Trash2 size={10} strokeWidth={2} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                              <button
                                onClick={() =>
                                  updateFooter({
                                    badges: [
                                      ...(config.footer.badges ?? []),
                                      { label: "New badge", key: makeKey() },
                                    ],
                                  })
                                }
                                className="text-[11px] font-bold text-tide hover:text-tide-d inline-flex items-center gap-1 transition-colors"
                              >
                                <Plus size={11} strokeWidth={2.5} />
                                Add badge
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Add section */}
              <div className="relative mb-3">
                <button
                  type="button"
                  onClick={() => setAddSectionOpen((o) => !o)}
                  disabled={availableFooterSections.length === 0}
                  className={`inline-flex items-center gap-1 text-[12px] font-bold transition-colors ${
                    availableFooterSections.length === 0
                      ? "text-ink-5 cursor-not-allowed"
                      : "text-rust hover:underline"
                  }`}
                >
                  <Plus size={12} strokeWidth={2.5} />
                  Add section
                </button>
                {addSectionOpen && availableFooterSections.length > 0 && (
                  <div className="absolute left-0 right-0 top-full z-50 mt-1 rounded-lg border border-ink-5 bg-paper shadow-lg py-1">
                    {availableFooterSections.map((s) => (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => {
                          addFooterSection(s.key);
                          setAddSectionOpen(false);
                        }}
                        className="w-full text-left px-3 py-1.5 hover:bg-paper-2 transition-colors"
                      >
                        <div className="text-[12px] font-semibold text-ink">
                          {s.label}
                        </div>
                        <div className="text-[10.5px] text-ink-4">
                          {s.description}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Layout capacity warning */}
              {Number.isFinite(FOOTER_LAYOUT_CAPACITY[config.footer.layout]) &&
                activeFooterSections.length >
                  (FOOTER_LAYOUT_CAPACITY[config.footer.layout] ?? 4) && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-800 mb-3">
                    This layout shows only the first{" "}
                    {FOOTER_LAYOUT_CAPACITY[config.footer.layout]} sections.
                    Switch layouts or remove sections to show the rest.
                  </div>
                )}

              {/* Legal links (bottom bar) */}
              <label className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mt-4 mb-[7px]">
                Bottom bar legal links
              </label>
              {(!config.footer.legalLinks || config.footer.legalLinks.length === 0) && (
                <p className="text-[10.5px] text-ink-5 leading-relaxed mb-1.5">
                  Showing default links (Terms, Privacy, Accessibility) until you add your own.
                </p>
              )}
              <div className="space-y-1 mb-1">
                {(config.footer.legalLinks ?? []).map((link, li) => (
                  <div
                    key={link.key}
                    className="flex items-center gap-1.5 border border-ink-6 rounded-lg bg-paper px-2 py-1.5"
                  >
                    <span className="flex-none cursor-grab text-ink-5 active:cursor-grabbing"
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = "move";
                        e.dataTransfer.setData("text/plain", String(li));
                        (e.currentTarget as HTMLElement).closest("[data-legal-link]")?.classList.add("opacity-40");
                      }}
                      onDragEnd={(e) => {
                        (e.currentTarget as HTMLElement).closest("[data-legal-link]")?.classList.remove("opacity-40");
                      }}
                    >
                      <GripVertical size={10} strokeWidth={2} />
                    </span>
                    <input
                      type="text"
                      value={link.label}
                      onChange={(e) =>
                        updateFooter({
                          legalLinks: (config.footer.legalLinks ?? []).map(
                            (l, i) =>
                              i === li
                                ? { ...l, label: e.target.value }
                                : l,
                          ),
                        })
                      }
                      placeholder="Label"
                      className="flex-1 min-w-0 bg-transparent border-0 text-[11px] font-semibold text-ink px-0 py-0 focus:outline-none focus:ring-0"
                    />
                    <DestinationTypeahead
                      value={link.path}
                      onChange={(path) =>
                        updateFooter({
                          legalLinks: (config.footer.legalLinks ?? []).map(
                            (l, i) =>
                              i === li
                                ? { ...l, path: path || "/" }
                                : l,
                          ),
                        })
                      }
                      placeholder="/path"
                      className="w-[72px] flex-none"
                    />
                    <button
                      onClick={() =>
                        updateFooter({
                          legalLinks: (config.footer.legalLinks ?? []).filter(
                            (_, i) => i !== li,
                          ),
                        })
                      }
                      className="flex-none text-ink-5 hover:text-rust transition-colors p-0.5 rounded"
                    >
                      <Trash2 size={10} strokeWidth={2} />
                    </button>
                  </div>
                ))}
              </div>
              <button
                onClick={() =>
                  updateFooter({
                    legalLinks: [
                      ...(config.footer.legalLinks ?? []),
                      { label: "New link", path: "/", key: makeKey() },
                    ],
                  })
                }
                className="text-[11px] font-bold text-tide hover:text-tide-d inline-flex items-center gap-1 transition-colors mb-3"
              >
                <Plus size={11} strokeWidth={2.5} />
                Add legal link
              </button>

              <div className="rounded-lg bg-tide-ss px-3 py-2.5 text-[11px] text-ink-3 leading-relaxed">
                Click a section in the preview to jump straight to it. Footer logo comes from{" "}
                <Link
                  to="/website/branding"
                  className="text-tide font-bold no-underline hover:underline"
                >
                  Branding
                </Link>
                .
              </div>
            </>
          )}
        </aside>
      </div>

      {/* React Router blocker */}
      <ConfirmDialog
        open={blocker.state === "blocked"}
        title="Unsaved changes"
        description="You have unsaved chrome changes that will be lost if you leave this page."
        variant="danger"
        confirmLabel="Leave"
        cancelLabel="Stay"
        onConfirm={() => blocker.proceed?.()}
        onCancel={() => blocker.reset?.()}
      />
    </div>
  );
}
